import {
  TrackUrlConfig,
  ConditionalTrackConfig,
} from "@/types/extensionSync";
import type { AtsNotificationChannelConfig } from "@/app/types/atsNotificationTypes";

// Role IDs
export const ROLES = {
  EXPERIMENTAL_OWNER:0,
  OWNER: 1,
  ADMIN: 2,
  HR: 3,
  OPERATIONS: 4,
  LEAD:5,
  FLEET_MANAGER: 8, 
  DRIVER: 10,
  APPLICANT: 11,
  // Provider roles (20-30)
  FLEET_PROVIDER_OWNER: 21,
  FLEET_PROVIDER_ADMIN: 22,
  FLEET_PROVIDER_TECHNICIAN: 23,
  FINANCE_PROVIDER_OWNER: 31,
  FINANCE_PROVIDER_ADMIN: 32,
  FINANCE_PROVIDER_ANALYST: 33,
  // LinqOps Support Provider roles (200-210)
  LINQOPS_SUPPORT_PROVIDER_OWNER: 200,
  LINQOPS_SUPPORT_PROVIDER_SALES_ADMIN: 201,
  LINQOPS_SUPPORT_PROVIDER_SALES_ASSOCIATE: 205,
  LINQOPS_SUPPORT_PROVIDER_SUPPORT_ADMIN: 206,
  LINQOPS_SUPPORT_PROVIDER_SUPPORT_ASSOCIATE: 210,
} as const;

// Role Names by ID
export const ROLE_NAMES: { [key: number]: string } = {
  0: 'Experimental Owner',
  1: 'Owner',
  2: 'Admin',
  3: 'HR',
  4: 'Operations',
  5: 'Lead',
  8: 'Fleet Manager',
  10: 'Driver',
  11: 'Applicant',
  // Provider roles
  21: 'Fleet Provider Owner',
  22: 'Fleet Provider Admin',
  23: 'Fleet Provider Technician',
  31: 'Finance Provider Owner',
  32: 'Finance Provider Admin',
  33: 'Finance Provider Analyst',
  // LinqOps Support Provider roles
  200: 'LinqOps Support Provider Owner',
  201: 'LinqOps Support Provider Sales Admin',
  205: 'LinqOps Support Provider Sales Associate',
  206: 'LinqOps Support Provider Support Admin',
  210: 'LinqOps Support Provider Support Associate',
};

/**
 * ROLE_RANGES.PROVIDER_* keys must stay in sync with PROVIDER_TYPES.
 * getProviderRoleRange builds the role-range key dynamically as `PROVIDER_${TYPE_KEY}`,
 * so whenever you add/update a provider type be sure to follow that naming convention.
 */
export const ROLE_RANGES = {
  PROVIDER: {
    MIN: 20,
    MAX: 210
  },
  PROVIDER_FLEET: {
    MIN: 20,
    MAX: 29
  },
  PROVIDER_FINANCE: {
    MIN: 30,
    MAX: 39
  },
  PROVIDER_LINQOPS_SUPPORT: {
    MIN: 200,
    MAX: 210
  }
} as const;

// Provider Types
export const PROVIDER_TYPES = {
  FLEET: "fleet",
  FINANCE: "finance",
  LINQOPS_SUPPORT: "linqopsSupport"
} as const;

export const VALID_PROVIDER_TYPES = Object.values(PROVIDER_TYPES) as readonly string[];

// Firebase Chat Message Sender Types for LinqOps Support Provider
// This array includes all historical and current names for LinqOps Support messages
// Used for backward compatibility - when reading messages, check against this array
// When writing new messages, always use PROVIDER_TYPES.LINQOPS_SUPPORT
// 
// To rename LinqOps Support Provider in the future:
// 1. Update PROVIDER_TYPES.LINQOPS_SUPPORT to the new name
// 2. Add the old value to this array (e.g., "linqopsSupport" when renaming)
// 3. No other code changes needed - backward compatibility is automatic
export const LINQOPS_SUPPORT_PROVIDER_SENDER_TYPES = [
  PROVIDER_TYPES.LINQOPS_SUPPORT,  // Current name
  "linqopsAdmin"                   // Legacy name for backward compatibility
] as const;

// Employee Status
export const STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  OFFBOARDED: 'OFFBOARDED',
  'Background Check in Progress': 'Background Check in Progress',
} as const;

// Inspection Execution Status Constants for Chrome Extension Sync
export const INSPECTION_EXECUTION_STATUS = {
  COMPLETED: "COMPLETED",
  PENDING: "PENDING",
  NO_DATA: "NO_DATA",
} as const;

export const AMAZON_OFFBOARDED_SOURCE = {
  FETCH_DSP_ASSOCIATES: 'fetchDSPAssociates',
  GET_OPERATIONAL_STATUS_BY_PROVIDER_ID: 'getOperationalStatusByProviderId',
  ROSTER_SYNC: 'rosterSync',
} as const;

// Fields merged across duplicate serviceStation rows when provisioning service
// areas — first non-null value per field wins (used by provisionMissingServiceAreas)
export const SERVICE_AREA_MERGEABLE_FIELDS = [
  "stationCode",
  "serviceAreaName",
  "isOperational",
  "serviceAreaTimeZoneId",
] as const;

// Default Values
export const DEFAULTS = {
  TIMEZONE: 'America/New_York',
} as const;

/**
 * Device type constants used to distinguish between personal devices (the
 * user's own phone/browser) and work devices (shared/handheld devices used
 * during a shift).
 *
 * The value is stored in `localStorage` under the key `"deviceType"` on the
 * client side (see `getClientDeviceType()` in utils.ts) and is sent to the
 * backend during token registration and subscription checks.
 *
 * - PERSONAL ('personal'): The user's personal phone or browser. Tokens for
 *   personal devices are stored in `users.notificationToken` (a simple string
 *   array) and have NO expiration — they persist until explicitly removed.
 *
 * - WORK ('work'): A shared/work device used during a shift. Tokens for
 *   work devices are stored in `users.workDeviceNotificationToken` (a JSONB
 *   array of `WorkDeviceTokenItem` objects) and EXPIRE at midnight in the
 *   user's timezone. A maximum of 5 work device tokens are kept per user
 *   (FIFO eviction).
 */
export const DEVICE_TYPE = {
  PERSONAL: 'personal',
  WORK: 'work',
} as const;

/**
 * Union type representing the two possible device types.
 * Used throughout the push notification flow to route requests to the correct
 * storage and retrieval logic.
 */
export type DeviceType = typeof DEVICE_TYPE[keyof typeof DEVICE_TYPE];

// Announcement types that support wave filtering in the Broadcast modal and API
export const ANNOUNCEMENT_TYPES_WITH_WAVES: readonly string[] = [
  "scheduled_today",
  "scheduled_tomorrow",
];

// System User ID
export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000001';
export const SYSTEM_USER_EMAIL = 'bot@linqops.com';

export const VEHICLE_STATUS = {
  INACTIVE: 'INACTIVE',
  ACTIVE: 'ACTIVE',
} as const;

export const VEHICLE_OPERATIONAL_STATUS = {
  OPERATIONAL: 'OPERATIONAL',
  GROUNDED: 'GROUNDED',
} as const;

export const EMPLOYEE_NOTES_TYPE = {
  ATTENDANCE: 'attendance',
  PERFORMANCE: 'performance',
  BEHAVIOR: 'behavior',
  FLEET: 'fleet',
  DOG_BITE: 'dog-bite',
  INCIDENT: 'incident',
  INJURY: 'injury',
  UNIFORM:'uniform',
  DRUG_TEST: 'drug-test',
  SCHEDULE:'schedule',
  TIMECARD: 'timecard',
  OTHER: 'other',
} as const;

// Attendance note type mapping for display labels
export const ATTENDANCE_TYPE_LABELS: Record<string, string> = {
  'call_out': 'Call Out',
  'late': 'Late',
  'no_show': 'No Show',
  'vto': 'VTO'
};

// Attendance note types
export const ATTENDANCE_NOTE_TYPES = {
  LATE: 'attendance/late',
  CALL_OUT: 'attendance/call_out',
  NO_SHOW: 'attendance/no_show',
  VTO: 'attendance/vto',
} as const;

// Attendance note types that mark a driver as absent (cannot back up routes).
// "late" is NOT included — a late driver still showed up and can still cover routes.
// Used by getDispatch, getWaveCount, and the dispatch page optimistic update
// to exclude absent backup drivers from the backup count.
export const ABSENT_ATTENDANCE_NOTE_TYPES: readonly string[] = [
  ATTENDANCE_NOTE_TYPES.CALL_OUT,
  ATTENDANCE_NOTE_TYPES.NO_SHOW,
  ATTENDANCE_NOTE_TYPES.VTO,
] as const;

export const APPLICANT_NOTES_TYPE = {
  ATTENDANCE: 'attendance',
  PERFORMANCE: 'performance',
  BEHAVIOR: 'behavior',
  FLEET: 'fleet',
  OTHER: 'other',
} as const;

export const FLEET_NOTES_TYPE = {
  REPAIR: "repair",
  PREVENTATIVE_MAINTENANCE: "preventative-maintenance",
  INSPECTION: "inspection",
  PAVE:"pave",
  ACCIDENT: "accident",
  DRIVER: "driver",
  DISPUTE:"dispute",
  TECHNICIAN: "technician",
  NETRADYNE: "netradyne",
  OTHER: "other",
};

export const OPERATION_DASHBOARD_OPTIONS = {
  ROUTES:"Routes",
  DAS:"DAs"
} as const;

export const FLEET_NOTES_SUB_TYPE = {
  DISPUTE: "dispute/afs",
};
export const FLEET_DOCUMENT_TYPE = {
  REGISTRATION: 'registration',
  INSURANCE: 'insurance',
  INSPECTION: 'inspection',
  RENTAL_AGREEMENT: 'rental-agreement',
  INVOICE: 'invoice',
  OTHER: 'other',
} as const;

export const EMPLOYEE_STATUS_TYPE = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
  Offboarded: 'OFFBOARDED',
  Onboarding: 'ONBOARDING',
} as const;

export const TERMINATION_TYPES = [
  "Voluntary Resignation",
  "Terminated",
  "Other"
] as const;

export const getStatusText = (progressStatus: string): string => {
  switch (progressStatus) {
    case "not_applicable":
      return "Not Applicable";
    case "complete":
      return "Complete";
    case "ahead":
      return "Ahead";
    case "on_time":
      return "On Time";
    case "behind":
      return "Behind";
    case "at_risk":
      return "At Risk";
    case "late_departing":
      return "Late Departing";
    case "not_found":
      return "Not Found";
    default:
      return progressStatus;
  }
};

export const FLEET_STATUS_TYPE = {
  INACTIVE:'INACTIVE',
  OPERATIONAL:'OPERATIONAL',
  ACTIVE:'ACTIVE',
  GROUNDED:'GROUNDED'
}

export const PAYROLL_PROVIDER = {
  PAYCOM: "Paycom",
  ADP: "ADP",
  UZIO: "Uzio",
};

export const ORGANIZATION_PAYROLL_PROVIDERS = {
  PAYCOM: "Paycom",
  ADP: "ADP",
  UZIO: "Uzio",
  OTHER: "Other"
};

// Fixed dropdown options for payFrequency — replaces free-text input to guarantee consistent values
// DB stores lowercase ("weekly", "bi-weekly"); labels show capitalized text for the UI
// Workers use case-insensitive match on "bi-weekly" to fetch 3 weeks of payroll data
export const PAY_FREQUENCY_OPTIONS = [
  { label: "Weekly", value: "weekly" },
  { label: "Bi-Weekly", value: "bi-weekly" },
];

export const PAYROLL_PAYCODE = {
  PTO: "PTO",
  TRN: "TRN",
  TRAINING: "TRAINING"
};

// NOTE: 'SL HOURLY' was removed from this list — it is not considered a salaried pay class.
// Earlier it was treated as salaried here; after a bug was found for PWOD it was moved
// to HOURLY_PAY_CLASSES below.
export const SALARIED_PAY_CLASSES = [
  'SAL',
  'SLE',
  'Salaried', //UZIO payroll provider
  'F SALARY'
] as const;

// Explicit list of pay classes that represent hourly employees
// WHY an explicit list: negating isSalaried or substring-matching "HOURLY" would
// wrongly classify unlisted/unknown pay classes as hourly.
export const HOURLY_PAY_CLASSES = [
  'F HOURLY',   // ADP full-time hourly
  'P HOURLY',   // ADP part-time hourly
  'S HOURLY',   // ADP seasonal hourly
  'SL HOURLY',  // previously in SALARIED_PAY_CLASSES; moved here after a bug was found for PWOD (as discussed with Neel) SL = Seasonal Hourly
  'Hourly',     // UZIO payroll provider
] as const;

// Default standard weekly hours for salaried employees when payroll provider API returns null
// Used by payroll sync workers as a fallback value for salaried employees
export const DEFAULT_SALARIED_STANDARD_HOURS = 40;

export const PAYROLL_ID_STATUS_TYPE = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
  Offboarded: 'OFFBOARDED',
}

export const CONTACT_SALES_CALENDLY_URL = "https://calendly.com/neelshah-connect/linqops-demo";
export const ONBOARDING_CALENDLY_URL = "https://calendly.com/linqops-sales/linqops-onboarding";

export const SUPPORT_PHONE = "+1 (888) 671-5467";
export const SUPPORT_EMAIL = "sales@linqops.com";

// Subscription Constants
export const SUBSCRIPTION = {
  FREE_TRIAL: {
    PLAN_TITLE: "Business Pro",
    DURATION_DAYS: 61, // Actual trial duration (61 days to ensure Stripe shows at least 60 days)
    DISPLAY_DURATION_DAYS: 60, // Marketing duration shown to users
    COUPON_NAME: "Free Trial",
    COUPON_DESCRIPTION: "100% off for 60-day free trial"
  },
  PAYROLL_COMPLIANCE: {
    PLAN_TITLE: "Payroll & Compliance"
  }
}

export const DRUG_TEST_RESULT_OPTIONS = [
  "Ordered",
  "At Lab",
  "Negative",
  "Positive",
  "Repeat",
]

// Incident / Accident / Injury - dropdown options
export const INCIDENT_ACCIDENT_TYPE_OPTIONS = [
  "Bitten or stung",
  "Body position/posture; bend/lean/twist",
  "Caught in, under, on, between",
  "Climbing",
  "Contact by",
  "Contact with chemicals",
  "Contact with electrical",
  "Contact with glass",
  "Contact with sharp object",
  "Contact with substance",
  "Contact with temperature extremes",
  "Exposure to",
  "Falls, different level",
  "Falls same level",
  "Forceful exertion/pushing/pulling",
  "Lowering/lifting/carrying",
  "Motor vehicle accident",
  "Repetition",
  "Rubbed of abraded",
  "Slip/trip, no fall",
  "Stepped on",
  "Struct against",
  "Struct by",
];

export const INCIDENT_INJURY_OPTIONS = [
  "Abrasion, Scratches (superficial)",
  "Allergic Reaction",
  "Amputation",
  "Avulsion",
  "Bite, Sting (insect or animal)",
  "Blister",
  "Bloodborne Pathogen Exposure",
  "Burn - Chemical",
  "Burn - Electrical/Electrocution",
  "Burn or Scald(heat)",
  "Concussion",
  "Contusion, Bruise",
  "Crushing Injury",
  "Cyst",
  "Dislocation",
  "Drowning",
  "Eye Irritation",
  "Foreign Body(ie splinter,sliver,chip,dust)",
  "Fracture",
  "Frostbite/Hypertherma",
  "Headache",
  "Hearing Loss, or Impairment",
  "Hernia",
  "Herniated/Displaced/Ruptured Disc",
  "laceration, Cut, Open Wound",
  "loss Of Consciousness",
  "Multiple Serious Injuries, Catastrophic Event",
  "Muscle or Connective tissue Tear or Detachment",
  "Poisoning",
  "Puncture",
  "Radiation Effects, Non-Ionizing",
  "Respiratory Irritation (e.g., Asthma)",
  "Skin Irritation",
  "Sprain",
  "Strain",
  "Strangulation,Asphyxia",
  "Tooth Chip/Break",
];

export const INCIDENT_ILLNESS_OPTIONS = [
  "Asbestosis",
  "Black Lung",
  "Bursitis",
  "Cancer",
  "Carpel Tunnel Syndrome",
  "Conjunctivitis",
  "Contagious or Infectious Disease",
  "Dermatitis",
  "Epicondylitis",
  "Hearing Loss, or Impairment",
  "Heart Attack",
  "Heat Stress/Exhaustion/Stroke",
  "Hematuria",
  "Mental Stress",
  "Musculoskeletal Disorder, not otherwise listed",
  "Nerve Compression Impingement",
  "Occupational Disease/Illness, not otherwise listed",
  "Pneumoconiosis",
  "Poisonina",
];

export const INCIDENT_CORE_ACTIVITY_OPTIONS = [
  "Acting/Performing",
  "Asbestos Removal",
  "Assembly Operation",
  "Blasting",
  "Blending",
  "Brazing",
  "Business Meeting/Event/Travel",
  "Cafeteria Operations",
  "Chemical Production Operations",
  "Cleaning",
  "Compounding",
  "Confined Spaces Operations",
  "Construction",
  "Customer Assistance",
  "Cutting Operations",
  "Demolition",
  "Disassembly Operations",
  "Equipment De-Installment",
  "Equipment Installation",
  "Equipment Operation",
  "Flight Operation",
  "Forming",
  "House Keeping",
  "Inspecting",
  "Laboratory Operations",
  "Lifting",
  "Loading/Unloading",
  "Machining",
  "Maintenance",
  "Material Handling",
  "Non Specific Site Activity",
  "Office Work",
  "Package Delivery",
  "Packaging",
  "Pipeline Operation",
  "Plating",
  "Press Operation",
  "Repair",
  "Resident Assistance",
  "Security/Emergency Response Operation",
  "Shipping/Receiving",
  "Studio Operations",
  "Stunt Operation",
  "Surface Cleaning",
  "Surface Coating",
  "Testing",
  "Vehicle Operations",
  "Waste Management",
  "Welding",
  "Woodworking",
];

export const INCIDENT_JOB_ACTIVITY_TYPE_OPTIONS = [
  "Not a normal part of job",
  "Normal part of job, performed routinely",
  "Normal part of job but performed infrequently",
];

export const INCIDENT_TIME_ON_JOB_TASK_OPTIONS = [
  "0-6 months",
  "6-12 months",
  "1-5 years",
  "5-10 years",
  "10-20 years",
  ">20 years",
];

export const INCIDENT_YEARS_OF_AMZ_DSP_SERVICE_OPTIONS = [
  "0-6 months",
  "6-12 months",
  "1-5 years",
  "5-10 years",
  "10-20 years",
  ">20 years",
];

export const INCIDENT_UNSAFE_CONDITION_OPTIONS = [
  "Administrative work place control not followed",
  "Allergic Reaction",
  "Congested or tight place",
  "Defective/worn/maladjusted facil, vehicle",
  "Defective/worn/maladjusted tool/equipment",
  "Ergonomically inadequate workstation or rotation",
  "Excessive noise/vibration/light; inadequate light",
  "Exposure to Animals/Insects/Plants",
  "Exposure to bloodborne pathogens",
  "Exposure to Extreme Weather",
  "Exposure to ionizing radiation",
  "Exposure to non-ionizing radiation",
  "Exposure to Smoke, dust, fumes",
  "Fire or explosion hazard",
  "Guest/Visitor Contact",
  "High or low air temperature",
  "High or low surface temperature",
  "Housekeeping, unsafe placement, slip/trip hazards",
  "Improperly fitting parts, materials, fasteners",
  "Inadequate guards, barriers, or warning system",
  "Inadequate time allotted to task",
  "Inadequate/Faculty/No PPE Assigned or available",
  "Loss of Utilities",
  "Overpressure/Underpressure",
  "Sharp edge on part, workstation, or equipment",
  "Spilled/splashed chemicals or leaking containers",
  "Unsafe food or drink",
  "Wet, icy, snowy road or walkway",
];

export const INCIDENT_UNSAFE_ACT_OPTIONS = [
  "Ascending/descending stairs improperly",
  "Assigned rotation or rest intervals not used",
  "Did not heed warning signals, lights, sounds",
  "Employee awareness/attentiveness",
  "Employee in danger zone/Line of Fire",
  "Equipment/tool used improperly",
  "Failed to execute LOTO",
  "Failed to use proper fall protection",
  "Failed to warn or barricade",
  "Horseplay or distracting behavior",
  "Improper force/posture/position",
  "Improper lifting",
  "Improper loading (overloading of racks/vehicles)",
  "Improperly placed/secured matls, tools, furniture",
  "Inadequate checks for obstacles in vehicle path",
  "Inadequate clearance for safely stopping vehicle",
  "Inattention to established procedures(SOPs, JSAs)",
  "Operated vehicle improperly",
  "Proper PPE not used for task",
  "Removed or disabled safety devices or guards",
  "Used area not intended for walking/working",
  "Wrong/defective item used",
];

export const INCIDENT_JOB_FACTOR_OPTIONS = [
  "Inadequate communication",
  "Inadequate Control System Setup or Failure",
  "Inadequate engineering",
  "Inadequate inspection/auditing",
  "Inadequate job planning, instruction, supervision",
  "Inadequate Mechanical Integrity / Maintenance management",
  "Inadequate procedures/work instruction, work standards",
  "Inadequate sourcing/supplier control",
  "Inadequately maintained tools/equipment",
  "Knowing misuse or abuse",
  "Work environment not controlled by business",
];

export const INCIDENT_PERSONAL_BEHAVIORAL_FACTOR_OPTIONS = [
  "Difficult for employee to execute",
  "Human Error",
  "Not under direct control of employee",
  "Lack of knowledge or skill",
  "Physical capability",
  "Physical or psychological stress",
];

export const INCIDENT_WORK_STATUS = {
  RETURN_TO_FULL_DUTY: {
    key: "Return to Full Duty",
    certificationText:
      "I hereby certify that, The on-the-job injury that I have reported is not impacting my current ability to work and I affirm that I am healthy to continue working my current schedule without restrictions.",
  },
  RETURN_WITH_RESTRICTIONS: {
    key: "Return With Restrictions",
    certificationText:
      "I hereby certify that, The on-the-job injury that I have reported is impacting my current ability to work and I affirm that I am not healthy to continue working my current schedule without restrictions.",
  },
  EXCUSED_FROM_WORK_UNTIL_NEXT_APPOINTMENT: {
    key: "Excused From Work Until Next Appointment",
    certificationText:
      "I hereby certify that, The on-the-job injury that I have reported is impacting my current ability to work and I affirm that I am not healthy to continue working my current schedule without restrictions.",
  },
} as const;

export const INTEGRATIONS_TYPE = {
  PAYCOM: "paycom",
  ADP: "adp",
  UZIO: "uzio",
  ZOHO_MAIL: "zoho_mail",
  FORWARD_EMAIL: "forward_email",
}

// Maps payroll integration types to the organization.payrollProvider value they
// are allowed to sync for. Prevents dual syncs when an org switches providers
// but the old integration is still active (e.g. ADP left on after moving to Uzio).
// Non-payroll types (zoho_mail, forward_email) are intentionally absent so they
// are never filtered by provider.
export const PAYROLL_INTEGRATION_PROVIDERS: Record<string, string> = {
  [INTEGRATIONS_TYPE.PAYCOM]: ORGANIZATION_PAYROLL_PROVIDERS.PAYCOM,
  [INTEGRATIONS_TYPE.ADP]: ORGANIZATION_PAYROLL_PROVIDERS.ADP,
  [INTEGRATIONS_TYPE.UZIO]: ORGANIZATION_PAYROLL_PROVIDERS.UZIO,
};

// Integration credential field requirements
export const INTEGRATION_CREDENTIAL_FIELDS = {
  PAYCOM: {
    CREDENTIALS: ['sid', 'token'],
  },
  ADP: {
    CREDENTIALS: ['client_id', 'client_secret'],
    CERTIFICATES: ['certificate_pem', 'private_key'],
  },
  // Uzio only requires an API key for authentication, no certificates needed
  UZIO: {
    CREDENTIALS: ['apiKey'],
  },
  FORWARD_EMAIL: {
    CREDENTIALS: ['apiToken', 'domain'],
  },
}
// Notification throttling time in minutes
export const NOTIFICATION_THROTTLE_TIME_MINUTES = 15;

// How long FCM keeps an undelivered push notification queued for an offline
// device (seconds). Without it, FCM defaults to 4 weeks and devices that were
// offline for days receive stale alerts. 1 hour keeps notifications
// time-relevant while still covering short connectivity gaps.
export const PUSH_NOTIFICATION_TTL_SECONDS = 60 * 60; // 1 hour

export const ORGANIZATION_TYPE = {
  PROVIDER: "provider",
}



// Email domain restriction for PROVIDER_LINQOPS_SUPPORT role
export const PROVIDER_LINQOPS_SUPPORT_EMAIL_DOMAIN = "@linqops.com";

// SMS-Related Keywords
// 10DLC Keywords
export const SMS_KEYWORDS = {
  OPT_IN: ['START','SUBSCRIBE'],
  OPT_OUT: ['STOP', 'UNSUBSCRIBE'],
  HELP: ['HELP'],
};

// Tollfree Keywords
export const TOLLFREE_SMS_KEYWORDS = {
  OPT_IN: ['START','UNSTOP'],
  OPT_OUT: ['STOP'],
  HELP: ['HELP'],
};

// Tenure Filter Ranges (in months)
export const TENURE_FILTERS = {
  NEW_HIRE: {
    MIN: 0,
    MAX: 2,
    LABEL: '0-2 months'
  },
  EARLY_TENURE: {
    MIN: 2,
    MAX: 4,
    LABEL: '2-4 months'
  },
  MID_TENURE: {
    MIN: 4,
    MAX: 6,
    LABEL: '4-6 months'
  },
  EXPERIENCED: {
    MIN: 6,
    MAX: null, // No upper limit
    LABEL: '6+ months'
  },
  OTHER: {
    LABEL: 'Others' // For cases where hire date is missing or invalid
  }
};

// Sources to exclude from Netradyne violations (considered non-Netradyne)
export const EXCLUDED_NETRADYNE_VIOLATION_SOURCES = ["Geotab"];

// Netradyne review status constants
export const NETRADYNE_REVIEW_STATUS = {
  DISPUTE_APPROVED: "Dispute Approved",
} as const;

// Dispatch status constants
export const DISPATCH_SHIFT_POSITION = {
  DISPATCHER: "DISPATCHER",
} as const;

export const EMPLOYEE_VAN_PREFERENCE = {
  SELECT: "Select",
  AMAZON_BRANDED: "Amazon Branded",
  RENTAL: "Rental",
}

// Payroll Sync Status Constants
export const PAYROLL_SYNC_TYPE = {
  ADP: "adp",
  PAYCOM: "paycom",
  UZIO: "uzio",
};

// Payroll Sync Schedule Hours (UTC times for cron jobs)
// Paycom runs at 13:00 and 00:00 UTC
// ADP runs at 00:00, 01:00, 14:00, 15:00 UTC (8 PM, 9 PM, 10 AM, 11 AM NY time)
// UZIO runs at 0:15 and 13:15 UTC (8:15 AM and 7:15 PM ET)
export const PAYROLL_SYNC_SCHEDULE_HOURS = {
  PAYCOM: [{ hour: 13, minute: 0 }, { hour: 0, minute: 0 }],
  ADP: [
    { hour: 0, minute: 0 },    // 8:00 PM NY (prev day) — evening run 1 (new)
    { hour: 1, minute: 0 },    // 9:00 PM NY (prev day) — evening run 2 (existing)
    { hour: 14, minute: 0 },   // 10:00 AM NY — morning run 1 (existing)
    { hour: 15, minute: 0 },   // 11:00 AM NY — morning run 2 (new)
  ],
  UZIO: [{ hour: 0, minute: 15 }, { hour: 13, minute: 15 }],
} as const;

export const PAYROLL_SYNC_STATUS = {
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
};

// Cron Job Constants
export const CRON_JOB_TYPES = {
  CREATE_AUTOMATED_COACHING_TICKETS: "createAutomatedCoachingTickets",
  ALERT_ENABLE_NOTIFICATION: "alertEnableNotification",
  WEEKLY_PERFORMANCE_NOTES: "weeklyPerformanceNotes",
  SEND_ALERTS_FOR_SCHEDULE: "sendAlertsForSchedule",
  AI_BATCH_INFERENCE: "aiBatchInference",
} as const;

export const CRON_JOB_STATUS = {
  COMPLETED: "completed",
  FAILED: "failed",
} as const;

// Notification Type Constants
export const MANAGEMENT_NOTIFICATION_TYPES = {
  CHAT: 'chat',
  EMPLOYEE_NOTES: 'employeeNotes',
  FLEET_NOTES: 'fleetNotes',
  FLEET_STATUS: 'fleetStatus',
} as const;

// Source values for the `source` parameter of sendNotificationToUserByEmail.
// This controls whether the function's internal SMS fallback triggers
// (it only fires when source === "chat"). ATS chat handles SMS fallback
// itself with org config overrides, so it uses GENERAL to disable the
// internal fallback — which queries the employee table (wrong for
// applicants) and calls sendSMS without org config overrides.
// Kept separate from MANAGEMENT_NOTIFICATION_TYPES because that constant
// is used as a type index for notification settings maps.
export const PUSH_NOTIFICATION_SOURCE = {
  CHAT: 'chat',
  GENERAL: 'general',
} as const;

// Notification Chat Source Constants
// Used as the `source` argument to sendSMS() to look up the Telnyx from-number
// and messaging profile. ATS chat uses the org's per-org smsTransactionConfig,
// but still passes "ats" as the source for logging and consistency.
export const MANAGEMENT_NOTIFICATION_CHAT_SOURCES = {
  DISPATCH: 'dispatch',
  HR: 'hr',
  ATS: 'ats',
} as const;

// SMS Transaction Purchase Status — lifecycle of an org's Telnyx number purchase
// stored in organization.smsTransactionConfig.purchaseStatus. Only "active" means
// the number is provisioned and ready to send. "pending" = purchase in progress,
// "failed" = purchase errored and needs retry. Callers must check for "active"
// before sending SMS through the org's dedicated number.
export const SMS_TRANSACTION_PURCHASE_STATUS = {
  PENDING: "pending",
  ACTIVE: "active",
  FAILED: "failed",
} as const;

// Management Notification Types Configuration
export const MANAGEMENT_NOTIFICATION_TYPES_CONFIG = {
  [MANAGEMENT_NOTIFICATION_TYPES.CHAT]: {
    label: 'Chat',
    sources: [MANAGEMENT_NOTIFICATION_CHAT_SOURCES.DISPATCH, MANAGEMENT_NOTIFICATION_CHAT_SOURCES.HR]
  },
  [MANAGEMENT_NOTIFICATION_TYPES.EMPLOYEE_NOTES]: {
    label: 'Employee Notes'
  },
  [MANAGEMENT_NOTIFICATION_TYPES.FLEET_NOTES]: {
    label: 'Fleet Notes'
  },
  [MANAGEMENT_NOTIFICATION_TYPES.FLEET_STATUS]: {
    label: 'Fleet Status'
  }
} as const;

export const FIREBASE_APP_INSTANCE_NAMES = {
  DEFAULT: "[DEFAULT]",
  ADMIN_SUPPORT: "adminSupport",
}

// Violation color constants for dispatch page tags
export const VIOLATION_COLORS = {
  DSB: {
    background: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200'
  },
  CDF: {
    background: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-300'
  },
  SAFETY: {
    background: 'bg-pink-100',
    text: 'text-pink-800',
    border: 'border-pink-200'
  },
  CC: {
    background: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-200'
  },
  // Changed from HOURS_EXCEEDED to HOURS_LIMIT to match settings page configuration keys
  HOURS_LIMIT: {
    background: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-200'
  },
  OODT: {
    background: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-200'
  },
  OVERTIME: {
    background: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-200'
  },
  ROLLING_7_DAYS: {
    background: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-200'
  },
  // Added qualification label colors for employee qualification labels shown on dispatch page
  CDV: {
    background: 'bg-lime-100',
    text: 'text-lime-800',
    border: 'border-lime-200'
  },
  STEP_VAN: {
    background: 'bg-indigo-100',
    text: 'text-indigo-800',
    border: 'border-indigo-200'
  },
  DOT: {
    background: 'bg-teal-100',
    text: 'text-teal-800',
    border: 'border-teal-200'
  },
  DVIC: {
    background: 'bg-cyan-100',
    text: 'text-cyan-800',
    border: 'border-cyan-200'
  },

  // Add more violation types as needed
} as const;

export type ViolationType = keyof typeof VIOLATION_COLORS;
// Added performance labels to allow organizations to enable/disable them in settings
// Performance labels configuration
export const PERFORMANCE_LABELS = {
  CC: {
    key: 'CC', // Short key shown in dispatch page
    label: 'Contact Compliance', // User-friendly name for settings checkboxes
    description: 'Issues with customer contact procedures (missing exemptions, incomplete communication attempts)',
  },
  OODT: {
    key: 'OODT',
    label: 'Out of Drive Time',
    description: 'Driver activity recorded outside of scheduled drive time windows',
  },
  DSB: {
    key: 'DSB',
    label: 'Delivery Success Behavior',
    description: 'Behavioral issues affecting delivery success and performance metrics',
  },
  CDF: {
    key: 'CDF',
    label: 'Customer Delivery Feedback',
    description: 'Negative feedback received from customers about delivery experience',
  },
  SAFETY: {
    key: 'SAFETY',
    label: 'Safety Violations',
    description: 'Traffic safety violations (speeding, stop signs, traffic lights, following distance)',
  },
  HOURS_LIMIT: {
    key: 'hours', // Will be prefixed with ">" in dispatch
    label: 'Hours Limit Exceeded',
    description: 'Drivers working more than the configured maximum hours on last working day',
  },
  OVERTIME: {
    key: 'OT', // Will be suffixed with threshold in dispatch
    label: 'Overtime',
    description: 'Drivers approaching or exceeding weekly overtime thresholds',
  },
  ROLLING_7_DAYS: {
    key: '7D', // Displayed as "Rolling 7 > X Hours" in dispatch
    label: 'Rolling 7 Days Hours',
    description: 'Drivers exceeding 7-day rolling hours threshold',
  },
  // These labels are derived from employee qualifications data and shown on dispatch page
  CDV: {
    key: 'CDV',
    label: 'CDV',
    description: 'Cargo Delivery Van qualification for drivers operating cargo delivery vehicles',
  },
  STEP_VAN: {
    key: 'STEP_VAN',
    label: 'Step Van',
    description: 'Step Van vehicle qualification for drivers operating step van vehicles',
  },
  DOT: {
    key: 'DOT',
    label: 'DOT',
    description: 'DOT Vehicle qualification for drivers operating Department of Transportation regulated vehicles',
  },
  DVIC: {
    key: 'DVIC',
    label: 'DVIC',
    description: 'Driver completed vehicle inspection within required time threshold (5 min for DOT, 1.5 min for non-DOT vehicles)',
  },

} as const;

export type PerformanceLabelsType = keyof typeof PERFORMANCE_LABELS;

// Coaching Settings Constants
export const COACHING_THRESHOLD_UNITS = {
  DAY: 'day',
  WEEK: 'week'
} as const;

// ATS Stage Config Types — determines the shape of the `config` JSONB column in atsStageConfigs
export const ATS_STAGE_CONFIG_TYPE = {
  FORM_SCHEMA: "form_schema",
  OFFER_TEMPLATE: "offer_template",
  TRAINING_MODULE: "training_module",
  EMAIL_RULE: "email_rule",
  NOTIFICATION_TEMPLATE: "notification_template",
} as const;

// Type moved to app/types/offerTemplateTypes.ts
export type { AtsStageConfigType } from "@/app/types/offerTemplateTypes";

// ATS Notification Template Channels — determines which delivery channel a
// notification template is for. Today only "email" is implemented; "sms" and
// "push" are placeholders for future support.
// TODO: When SMS is implemented, add conditional validation in the validator
//   (smsBody required when channel="sms") and conditional send logic in
//   atsNotificationService (resolve phone numbers, call Telnyx instead of SES).
// TODO: When push is implemented, add pushBody field + push send logic.
export const ATS_NOTIFICATION_TEMPLATE_CHANNEL = {
  EMAIL: "email",
  SMS: "sms",     // TODO: future — Telnyx SMS templates
  PUSH: "push",   // TODO: future — push notification templates
} as const;

// ATS Notification Trigger Events — categorizes notification templates by the
// lifecycle event that fires them. Used by the Part 2 UI to group/filter
// templates in the settings tab. Not used by the send path (the call site
// already knows which template it wants).
export const ATS_NOTIFICATION_TRIGGER_EVENT = {
  STAGE_ENTERED: "stage_entered",
  STAGE_EXITED: "stage_exited",
  INTERVIEW_BOOKED: "interview_booked",
  MAGIC_LINK: "magic_link",
  OFFER_SENT: "offer_sent",
  REJECTION: "rejection",
  // Follow-up notifications sent to candidates after a delay (e.g. day 3, day 6).
  // Used by the StageModal Follow-Up Notifications section to filter templates.
  FOLLOW_UP: "follow_up",
  // Applicant welcome email sent when an account is provisioned for an applicant.
  APPLICANT_WELCOME: "applicant_welcome",
  // Work email provisioning failed — sent to recruiter with error details
  WORK_EMAIL_PROVISION_FAILED: "work_email_provision_failed",
} as const;

// Prefix used to identify stage-related trigger events. The StageModal
// notification template dropdown filters to templates whose triggerEvent
// starts with this string, so future stage_* events (e.g. stage_reminder)
// are included automatically without code changes.
export const ATS_NOTIFICATION_TRIGGER_EVENT_PREFIX = {
  STAGE: "stage_",
} as const;


// Human-readable labels for each trigger event — used by the Notification
// Templates tab table to show recruiters what fires each template instead of
// the raw snake_case value.
export const ATS_NOTIFICATION_TRIGGER_EVENT_LABELS: Record<string, string> = {
  [ATS_NOTIFICATION_TRIGGER_EVENT.STAGE_ENTERED]: "Stage Entered",
  [ATS_NOTIFICATION_TRIGGER_EVENT.STAGE_EXITED]: "Stage Exited",
  [ATS_NOTIFICATION_TRIGGER_EVENT.INTERVIEW_BOOKED]: "Interview Booked",
  [ATS_NOTIFICATION_TRIGGER_EVENT.MAGIC_LINK]: "Magic Link",
  [ATS_NOTIFICATION_TRIGGER_EVENT.OFFER_SENT]: "Offer Sent",
  [ATS_NOTIFICATION_TRIGGER_EVENT.REJECTION]: "Rejection",
  [ATS_NOTIFICATION_TRIGGER_EVENT.FOLLOW_UP]: "Follow-Up",
  [ATS_NOTIFICATION_TRIGGER_EVENT.APPLICANT_WELCOME]: "Applicant Welcome",
  [ATS_NOTIFICATION_TRIGGER_EVENT.WORK_EMAIL_PROVISION_FAILED]: "Work Email Provision Failed",
};

// Dropdown options for the Trigger Event field in the Notification Template
// modal — derived from the constant so a new trigger event only needs to be
// added once (in ATS_NOTIFICATION_TRIGGER_EVENT + its label above).
// Same pattern as ATS_STAGE_TYPE_DROPDOWN_OPTIONS.
export const ATS_NOTIFICATION_TRIGGER_EVENT_OPTIONS: Array<{
  value: string;
  label: string;
}> = Object.values(ATS_NOTIFICATION_TRIGGER_EVENT).map((value) => ({
  value,
  label: ATS_NOTIFICATION_TRIGGER_EVENT_LABELS[value] ?? value,
}));

// Dropdown options for the Channel field in the Notification Template modal —
// derived from ATS_NOTIFICATION_TEMPLATE_CHANNEL so future channels appear
// automatically. Only "email" actually delivers today; sms/push are saved for
// future use (sendNotification ignores config.channel).
export const ATS_NOTIFICATION_CHANNEL_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  { value: ATS_NOTIFICATION_TEMPLATE_CHANNEL.EMAIL, label: "Email" },
  { value: ATS_NOTIFICATION_TEMPLATE_CHANNEL.SMS, label: "SMS" },
  { value: ATS_NOTIFICATION_TEMPLATE_CHANNEL.PUSH, label: "Push Notification" },
];

// Merge variables available in notification templates — the sendNotification
// service substitutes {{var}} placeholders with these values. Shown as hint
// text in the Notification Template modal so recruiters know what they can use.
// Must stay in sync with the MergeVars type in app/types/atsNotificationTypes.ts.
export const ATS_NOTIFICATION_MERGE_VARS = [
  "firstName",
  "lastName",
  "portalUrl",
  "orgName",
  "toStageName",
  "position",
  "payRate",
  "startDate",
  "slotStartTime",
  "slotLocation",
  "slotMode",
  "email",
  "password",
  "loginUrl",
  "dspShortCode",
] as const;

// ATS Notification Channels — the delivery channels supported by the
// centralized ATS notification service (atsNotificationService.sendNotification).
//   "email"      → single email to personalEmail (SES)
//   "email_dual" → dual email to workEmail + personalEmail (SES, 2 rows)
//   "sms"        → SMS to applicant.phone (Telnyx, requires smsConsent)
//   "push"       → push to users.notificationToken (FCM, requires applicant user row)
// "email_dual" is a delivery variant governed by the "email" org toggle, not its own.
// "push" requires the applicant to have a users row (ats.access.enableApplicantLogin=true)
// and a registered FCM token (portal push infra — work in progress).
export const ATS_NOTIFICATION_CHANNEL = {
  EMAIL: "email",
  EMAIL_DUAL: "email_dual",
  SMS: "sms",
  PUSH: "push",
} as const;

// ATS Notification Channel Toggles — the three channels that can be independently
// enabled/disabled at the organization level via the `ats` JSONB column.
// Distinct from ATS_NOTIFICATION_CHANNEL (which includes "email_dual", a delivery
// variant governed by the "email" toggle, not its own toggle).
// Type lives in atsNotificationTypes.ts alongside AtsNotificationChannel.
export const ATS_NOTIFICATION_CHANNEL_TOGGLE = {
  sms: "sms",
  push: "push",
  email: "email",
} as const;

// Default ATS notification channel config — all channels enabled.
// Used when `ats` is null/empty or `ats.notifications` is missing so existing
// organizations keep receiving notifications until an admin explicitly disables
// a channel. Prevents a silent regression where ATS chat notifications never
// fire for orgs that haven't configured the `ats` JSONB column yet.
// Type lives in atsNotificationTypes.ts alongside AtsNotificationChannelConfig.
export const DEFAULT_ATS_NOTIFICATION_CONFIG: AtsNotificationChannelConfig = {
  enableSms: true,
  enablePush: true,
  enableEmail: true,
}

// Default Applicant Welcome Email Template — seeded for every new org via
// seedApplicantWelcomeTemplate. Referenced by the notification template service
// and potentially other call sites that need the default template definition.
export const DEFAULT_APPLICANT_WELCOME_TEMPLATE = {
  name: "Applicant Welcome Email",
  triggerEvent: ATS_NOTIFICATION_TRIGGER_EVENT.APPLICANT_WELCOME,
  templateKey: ATS_NOTIFICATION_TRIGGER_EVENT.APPLICANT_WELCOME,
  channel: ATS_NOTIFICATION_TEMPLATE_CHANNEL.EMAIL,
  subject: "[{{dspShortCode}}] {{orgName}} - Welcome to LinqOps",
  htmlBody: `<p>Hello {{firstName}} {{lastName}},</p>
<p>Your LinqOps account has been successfully created by <strong>{{orgName}}</strong>.</p>
<p>You can now log in to your account using the following link:
<br>Website: <a href="{{loginUrl}}">{{loginUrl}}</a>
</p>
<p>Here are your login credentials:
<br>DSP Short Code: {{dspShortCode}}
<br>Email: {{email}}
<br>Password: {{password}}</p>
<p>Best regards,
<br><strong>Team LinqOps.</strong></p>`,
  description:
    "Sent to applicants when their LinqOps user account is created upon application.",
};

// Non-production allowed email domains — used by isAllowedNonProdEmail (shared.ts)
// to gate email sends in dev/staging to internal addresses only. Prevents
// accidentally emailing real candidates from non-production environments.
export const NON_PROD_ALLOWED_EMAIL_DOMAINS = ["@linqops.com", "@thirdrocktechkno.com"];

// ATS Notification log statuses — the lifecycle of a notification log row.
// Flow: queued → sent → delivered → opened (happy path)
//       queued → sent → bounced (SES bounce)
//       queued → failed (SES send error or gate block)
export const ATS_NOTIFICATION_STATUS = {
  QUEUED: "queued",
  SENT: "sent",
  DELIVERED: "delivered",
  OPENED: "opened",
  BOUNCED: "bounced",
  FAILED: "failed",
} as const;

// ATS Pipeline view modes
export const ATS_VIEW_MODES = {
  BOARD: "board",
  TABLE: "table",
} as const;
export type AtsViewMode = (typeof ATS_VIEW_MODES)[keyof typeof ATS_VIEW_MODES];

// ATS Merge Fields — available fields for offer letter templates
// Used in OfferTemplateBuilder.tsx and load-editor route for label mapping
export const ATS_MERGE_FIELDS = [
  { key: "system.currentDate", label: "Today's Date" },
  { key: "applicant.legalName", label: "Applicant Full Name" },
  { key: "applicant.phone", label: "Applicant Phone Number" },
  { key: "applicant.email", label: "Applicant Email Address" },
  { key: "applicant.addressFull", label: "Applicant Address" },
  { key: "offer.position", label: "Job Position" },
  { key: "offer.payRate", label: "Pay Rate" },
  { key: "offer.startDate", label: "Employment Start Date" },
  { key: "org.name", label: "Company Name" },
  { key: "org.addressFull", label: "Company Address" },
] as const;

// Map ATS merge field keys to display labels
export const ATS_MERGE_FIELD_LABELS: Record<string, string> = Object.fromEntries(
  ATS_MERGE_FIELDS.map((f) => [f.key, f.label])
);

// ATS Group Field Types — Documenso grouped-option fields
// CHECKBOX = multi-select, RADIO = single-select
export const ATS_GROUP_FIELD_TYPE = {
  CHECKBOX: "CHECKBOX",
  RADIO: "RADIO",
} as const;

export type AtsGroupFieldType = (typeof ATS_GROUP_FIELD_TYPE)[keyof typeof ATS_GROUP_FIELD_TYPE];

/** True when the field type is CHECKBOX (case-insensitive — editor uses lowercase, Documenso uppercase). */
export const isCheckboxFieldType = (type: string | null | undefined): boolean =>
  String(type ?? "").toUpperCase() === ATS_GROUP_FIELD_TYPE.CHECKBOX;

/** True when the field type is RADIO (case-insensitive). */
export const isRadioFieldType = (type: string | null | undefined): boolean =>
  String(type ?? "").toUpperCase() === ATS_GROUP_FIELD_TYPE.RADIO;

/** True for grouped-option field types (CHECKBOX or RADIO). Case-insensitive. */
export const isGroupFieldType = (type: string | null | undefined): boolean =>
  isCheckboxFieldType(type) || isRadioFieldType(type);

// ATS Group Field Direction — layout for checkbox/radio groups
export const ATS_GROUP_DIRECTION = {
  VERTICAL: "vertical",
  HORIZONTAL: "horizontal",
} as const;

export type AtsGroupDirection = (typeof ATS_GROUP_DIRECTION)[keyof typeof ATS_GROUP_DIRECTION];

/** Normalize a direction value — anything except "horizontal" resolves to "vertical". */
export const normalizeGroupDirection = (direction: string | null | undefined): AtsGroupDirection =>
  String(direction ?? "").toLowerCase() === ATS_GROUP_DIRECTION.HORIZONTAL
    ? ATS_GROUP_DIRECTION.HORIZONTAL
    : ATS_GROUP_DIRECTION.VERTICAL;

export const COACHING_THRESHOLD_UNIT_OPTIONS = Object.values(COACHING_THRESHOLD_UNITS);

export const EXPERIMENTAL_ORG_IDS = [
  '25b5e67b-4064-4e67-9d75-151e4f8e19ff', // LQLC
  'c28fd1d7-4518-463b-a609-ed8cfb99240d', // LMDL
  '1fc07e27-9784-4926-ae23-9a251ffd1867' // DEMO
] as const;

// ATS Short Link — character set for random short code generation
// URL-safe unambiguous: no 0/O/1/l/I to avoid human confusion
export const SHORT_LINK_ALPHABET = "abcdefghijkmnpqrstuvwxyz23456789";

// ATS Short Link — length of the generated short code
export const SHORT_LINK_CODE_LENGTH = 6;

// ATS Short Link — valid target types for a short link.
// "job_requisition" = link to a specific job posting (requires targetId).
// "careers_page"    = link to the org-wide careers page (targetId is null).
export const SHORT_LINK_TARGET_TYPES = ["job_requisition", "careers_page"] as const;

// Short link target type enum — use SHORT_LINK_TARGET_TYPE.CAREERS_PAGE / .JOB_REQUISITION
// in components instead of string literals so refactors / renames are caught by the compiler.
export const SHORT_LINK_TARGET_TYPE = {
  CAREERS_PAGE: "careers_page",
  JOB_REQUISITION: "job_requisition",
} as const;

// ATS Portal Token — valid lifecycle statuses for a magic link token.
// "active"  = candidate can use the link
// "used"    = candidate clicked it (still valid for session)
// "revoked" = invalidated (rejection, manual admin)
// "expired" = past expiresAt — marked by daily cron
export const PORTAL_TOKEN_STATUSES = ["active", "used", "revoked", "expired"] as const;
// ATS Applicant History — values used in the atsApplicantHistories table.
// The full list of valid event types, actor types, and reasons is documented in
// the schema comment at atsApplicantHistories (schema.ts line 2755).
// Only values currently used in the codebase are defined here — add more as needed.
export const ATS_HISTORY_EVENT_TYPE = {
  STAGE_TRANSITION: "stage_transition",
  INTERVIEW_BOOKED: "interview_booked",
  INTERVIEW_NO_SHOW: "interview_no_show",
  INTERVIEW_COMPLETED: "interview_completed",
  // A recruiter manually sent a stage follow-up email from the candidate drawer.
  // Separate from stage_transition so the timeline shows it as a communication
  // event, not a stage move.
  FOLLOW_UP_SENT: "follow_up_sent",
  // A work email address was recorded against the applicant. Written by the manual
  // assign/clear route so an address typed in by hand is traceable to a person.
  EMAIL_PROVISIONED: "email_provisioned",
  // A candidate's questionnaire file answer was verified + saved at form
  // submit (logged from submit-form, NOT at presign — presign only means
  // "asked for a URL", not "file was uploaded"). Audit trail for the public
  // upload surface: who uploaded what, when, into which application.
  FILE_UPLOADED: "file_uploaded",
} as const;

// Actor types — WHO performed the action. Single source of truth for the
// AtsHistoryActorType type in atsPipelineTypes.ts (derived via `typeof`).
//   - SYSTEM    = automation engine, cron job, webhook (no human initiator)
//   - USER      = recruiter/admin operating in the ATS dashboard
//   - APPLICANT = candidate acting via the portal (form submit, offer sign/decline)
export const ATS_HISTORY_ACTOR_TYPE = {
  SYSTEM: "system",
  USER: "user",
  APPLICANT: "applicant",
} as const;

// ATS History Actor IDs — free-text identifiers used when actorType is "system"
// (no user.id or applicant.id available). Stored in atsApplicantHistories.actorId
// so the audit trail can distinguish which automated system performed the action.
//   - DOCUMENSO_WEBHOOK = Documenso fired the event, but recipient email didn't
//                         match any applicant or user (unknown signer)
//   - KNOCKOUT_ENGINE   = Screening Scoring & Knockout Engine auto-rejected the
//                         candidate based on a knockout rule in the screening form
export const ATS_HISTORY_ACTOR_ID = {
  DOCUMENSO_WEBHOOK: "documenso-webhook",
  KNOCKOUT_ENGINE: "knockout-engine",
} as const;

// ATS Rejection Reasons — stored in atsApplications.rejectedReason.
// Drives the "Rejection Reasons" report (most actionable given 93% rejection rate).
// Full list documented in the schema comment at atsApplications (schema.ts line 2094).
export const ATS_REJECTION_REASON = {
  MIN_AGE_DRIVER: "min_age_driver",
  NO_VALID_LICENSE: "no_valid_license",
  LICENSE_TENURE: "license_tenure",
  NO_WORK_AUTH: "no_work_auth",
  PHYSICAL_REQ: "physical_req",
  DRUG_SCREEN: "drug_screen",
  AVAILABILITY: "availability",
  MVR_RISK: "mvr_risk",
  NO_SHOW: "no_show",
  FAILED_INTERVIEW: "failed_interview",
  OFFER_REJECTED: "offer_rejected",
  BGC_FAILED: "bgc_failed",
  DRUG_TEST_POSITIVE: "drug_test_positive",
  WITHDREW: "withdrew",
  OTHER: "other",
} as const;

// ATS Rejection Reason Labels — human-friendly labels for the rejection reason
// codes above. Used in the rejection modal dropdown (RejectApplicationModal.tsx).
export const ATS_REJECTION_REASON_LABELS: { value: string; label: string }[] = [
  { value: ATS_REJECTION_REASON.MIN_AGE_DRIVER, label: "Under Minimum Age for Driving" },
  { value: ATS_REJECTION_REASON.NO_VALID_LICENSE, label: "No Valid Driver's License" },
  { value: ATS_REJECTION_REASON.LICENSE_TENURE, label: "Insufficient License Tenure" },
  { value: ATS_REJECTION_REASON.NO_WORK_AUTH, label: "No Work Authorization" },
  { value: ATS_REJECTION_REASON.PHYSICAL_REQ, label: "Does Not Meet Physical Requirements" },
  { value: ATS_REJECTION_REASON.DRUG_SCREEN, label: "Failed Drug Screen" },
  { value: ATS_REJECTION_REASON.AVAILABILITY, label: "Availability Does Not Match" },
  { value: ATS_REJECTION_REASON.MVR_RISK, label: "MVR Risk — Driving Record" },
  { value: ATS_REJECTION_REASON.NO_SHOW, label: "No Show" },
  { value: ATS_REJECTION_REASON.FAILED_INTERVIEW, label: "Failed Interview" },
  { value: ATS_REJECTION_REASON.OFFER_REJECTED, label: "Offer Rejected by Candidate" },
  { value: ATS_REJECTION_REASON.BGC_FAILED, label: "Failed Background Check" },
  { value: ATS_REJECTION_REASON.DRUG_TEST_POSITIVE, label: "Drug Test Positive" },
  { value: ATS_REJECTION_REASON.WITHDREW, label: "Candidate Withdrew" },
  { value: ATS_REJECTION_REASON.OTHER, label: "Other" },
];

export const ATS_STAGE_TRANSITION_REASON = {
  APPLIED: "applied",
  FORM_SUBMITTED: "form_submitted",
  OFFER_SENT: "offer_sent",
  OFFER_SIGNED: "offer_signed",
  OFFER_DECLINED: "offer_declined",
  RECRUITER_ADVANCE: "recruiter_advance",
  RECRUITER_REJECT: "recruiter_reject",
  AUTO_REJECT_KNOCKOUT: "auto_reject_knockout",
  SLA_TIMEOUT: "sla_timeout",
  INTERVIEW_BOOKED: "interview_booked",
  INTERVIEW_COMPLETED: "interview_completed",
  INTERVIEW_NO_SHOW: "interview_no_show",
} as const;

// ATS Portal Error — reasons shown to the candidate when their magic link fails.
// Used by the PortalError component in portal/[token]/page.tsx.
// Each value maps to a specific user-facing message so the candidate knows
// exactly what happened (rejected vs. expired vs. rate-limited vs. invalid).
export const PORTAL_ERROR_REASON = {
  INVALID: "invalid",          // Token not found in DB (wrong URL, deleted, never existed)
  EXPIRED: "expired",          // Token past expiresAt (14-day TTL)
  REVOKED: "revoked",          // Token manually revoked (not rejection — e.g., admin reissue)
  REJECTED: "rejected",        // Token revoked because application status = "rejected"
  RATE_LIMITED: "rate_limited", // Too many requests from this IP (brute-force protection)
} as const;

// =============================================================================
// PUBLIC ROUTES — Pages that should NOT receive any admin context providers
// =============================================================================
// WHY THIS EXISTS:
//   The root layout (app/layout.tsx) wraps EVERY page with <AppProviders>.
//   AppProviders includes <UserContextProvider>, which automatically calls
//   GET /api/user on mount if a logged-in session exists. This fetches
//   sensitive admin data (Stripe customer ID, role permissions, subscription
//   plan) into the browser.
//
//   Public pages like the candidate portal (/portal/[token]) and the careers
//   page (/careers/[orgSlug]) do NOT use any of this data — they are
//   completely separate systems (token-based auth for portal, no auth for
//   careers). But because they share the root layout, the provider runs
//   anyway and fetches admin data unnecessarily.
//
//   This happens when an admin is logged in and visits a public page:
//   their full user/role/subscription data gets fetched and sits in the
//   browser context, visible in DevTools, even though the page never uses it.
//
//   This array lists the URL prefixes for public pages. When a page's URL
//   starts with one of these prefixes, AppProviders skips ALL providers and
//   renders the page content directly — no UserContextProvider, no
//   NotificationProvider, no ChatProvider, no AdminSupportChatProvider.
//
//   Admin pages (everything NOT in this list) still get all providers as
//   before — this change does NOT affect any authenticated page.
//
// HOW TO UPDATE:
//   If you add a new public page in the future (e.g., /referrals/), add its
//   prefix to this array. If you forget, the new page will accidentally
//   fetch admin data — the exact bug this fix prevents.
//
// NOTE: /s/ is NOT included here because /s/[code] is a route.ts (API route),
// not a page.tsx. API routes do not render through the React layout tree, so
// they never go through AppProviders. Only page.tsx files are affected.
// =============================================================================
export const PUBLIC_ROUTES = ["/portal/", "/careers/"] as const;

// ATS Job Requisition — lifecycle statuses
// "draft"     = not visible publicly (recruiter is still editing)
// "published" = visible on the public careers page
// "paused"    = temporarily hidden from the careers page (can be re-published)
// "closed"    = no more applicants accepted (hiring complete or cancelled)
export const ATS_JOB_REQUISITION_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  PAUSED: "paused",
  CLOSED: "closed",
} as const;

// ATS Job Requisition — employment types for job postings
// Used in the Job Requisition modal as a multi-select checkbox group so
// recruiters can select multiple employment types for a single job.
// The candidate's answer to a questionnaire question (configured as a
// condition on the pipeline's esign stage) determines which offer template
// is used at offer-send time — the employment types here are display/filter
// metadata only, not offer-template routing keys.
export const ATS_EMPLOYMENT_TYPES = [
  "Full-time",
  "Part-time",
  "Seasonal",
  "Contract",
  "Temporary",
] as const;

// ATS Job Requisition — pay rate entry modes
// "rate"  = single pay rate (stored in payRateMin, payRateMax cleared)
// "range" = pay range with min + max (stored in payRateMin + payRateMax)
export const ATS_PAY_RATE_MODE = {
  RATE: "rate",
  RANGE: "range",
} as const;

// ATS Job Requisition — status badge styles
// Maps job requisition status to Tailwind CSS classes for visual styling in the table
export const ATS_JOB_REQUISITION_STATUS_STYLES: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  published: "bg-green-50 text-green-700",
  paused: "bg-yellow-50 text-yellow-700",
  closed: "bg-red-50 text-red-700",
};

// ATS Offer — lifecycle statuses stored in atsStageInstances.data.offerStatus
// "sent"      = offer letter sent to candidate via Documenso (awaiting signature)
// "signed"    = at least one recipient signed (partial — multi-signer pending)
// "completed" = all recipients signed (offer fully executed)
// "declined"  = candidate declined the offer
// "rejected"  = same as declined (legacy alias — Documenso fires "rejected" event)
export const ATS_OFFER_STATUS = {
  SENT: "sent",
  SIGNED: "signed",
  COMPLETED: "completed",
  DECLINED: "declined",
  REJECTED: "rejected",
} as const;

// ATS Offer — status badge styles
// Maps offer status to label + Tailwind CSS classes for visual styling on candidate cards
export const ATS_OFFER_STATUS_STYLES: Record<string, { label: string; class: string }> = {
  sent: { label: "Offer Sent", class: "bg-blue-50 text-blue-700" },
  signed: { label: "Offer Partially Signed", class: "bg-yellow-50 text-yellow-700" },
  completed: { label: "Offer Letter Completed", class: "bg-green-50 text-green-700" },
  declined: { label: "Declined", class: "bg-red-50 text-red-700" },
  rejected: { label: "Declined", class: "bg-red-50 text-red-700" },
};

// ATS Document — human-readable labels stored on the stage instance at send
// time (data.documentLabel) and used for Documenso doc titles, email subjects,
// download filenames, and card badge wording.
export const ATS_DOCUMENT_LABEL = {
  OFFER_LETTER: "Offer Letter",
  DOCUMENT: "Document",
} as const;

// ATS Document — status badge styles for NON-offer document stages
// (sexual_harassment, etc.) — same statuses, generic "document" wording so
// "offer letter" never appears on non-offer stages.
export const ATS_DOCUMENT_STATUS_STYLES: Record<string, { label: string; class: string }> = {
  sent: { label: "Document Sent", class: "bg-blue-50 text-blue-700" },
  signed: { label: "Document Partially Signed", class: "bg-yellow-50 text-yellow-700" },
  completed: { label: "Document Completed", class: "bg-green-50 text-green-700" },
  declined: { label: "Declined", class: "bg-red-50 text-red-700" },
  rejected: { label: "Declined", class: "bg-red-50 text-red-700" },
};

// ===========================================================================
// ATS APPLICATION STATUS CONSTANTS
// Used throughout the ATS system to represent the lifecycle status of an
// application (atsApplications.status). These values determine the visual
// representation and behavior of applications in the recruiter board and
// applicant portal.
// ===========================================================================

// ATS Application Status — lifecycle statuses stored in atsApplications.status
// "active"    = application is in progress (applicant is moving through stages)
// "rejected"  = application was rejected (applicant will not advance further)
// "hired"     = application resulted in hire (applicant became an employee)
// "on_hold"   = application is paused (may resume later)
// "withdrawn" = applicant withdrew their application
export const ATS_APPLICATION_STATUS = {
  ACTIVE: "active",
  REJECTED: "rejected",
  HIRED: "hired",
  ON_HOLD: "on_hold",
  WITHDRAWN: "withdrawn",
} as const;

// ATS Application Status — display labels and badge styles for the applicant portal
// Maps status values to user-facing labels and Tailwind CSS classes for visual styling.
// Used by the Application Status page to show applicants their current status.
export const ATS_APPLICATION_STATUS_STYLES: Record<
  string,
  { label: string; badgeClass: string }
> = {
  [ATS_APPLICATION_STATUS.ACTIVE]: {
    label: "In Progress",
    badgeClass:
      "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  },
  [ATS_APPLICATION_STATUS.REJECTED]: {
    label: "Not Selected",
    badgeClass: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  },
  [ATS_APPLICATION_STATUS.HIRED]: {
    label: "Hired",
    badgeClass: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  },
  [ATS_APPLICATION_STATUS.ON_HOLD]: {
    label: "On Hold",
    badgeClass: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  },
  [ATS_APPLICATION_STATUS.WITHDRAWN]: {
    label: "Withdrawn",
    badgeClass: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  },
};

// ===========================================================================
// AMAZON ONBOARDING INTEGRATION
// Used by recruiters to quickly access the Amazon DSP onboarding portal
// from the ATS interface (stage card and candidate drawer).
// ===========================================================================

// Amazon DSP Onboarding Portal URL
// This is a static URL (same for all organizations) that opens the
// "Add Delivery Associates" page where recruiters can submit applicant
// information for DSP onboarding. The button appears in two locations:
//   1. Stage card in Settings/Pipelines (for integration_amazon stages)
//   2. Candidate drawer Overview tab (when candidate is in Amazon stage)
// Opens in a new tab so recruiter doesn't lose the ATS context.
export const AMAZON_ONBOARDING_URL = "https://logistics.amazon.com/account-management/delivery-associates/add";

// ===========================================================================
// ATS CHAT CONSTANTS
// Used by the ATS chat feature (recruiter ↔ applicant messaging in the
// pipeline drawer + applicant portal). Separate from Dispatch/HR chat —
// the two systems share Firebase as the storage layer but use different
// path prefixes (ats-{applicationId} vs {employeeId}) and sentBy values.
// ===========================================================================

// Deep-link routes for chat navigation from push notifications.
// When a user taps a chat notification, the deep link opens the correct page
// with the applicationId as a query param so the conversation auto-selects.
export const ATS_CHAT_PATHS = {
  RECRUITER: "/ats/pipeline",
  APPLICANT: "/applicant/chat",
} as const;

// Who sent a message — stored in Firebase as the `sentBy` field.
// Uses "hr" (not "recruiter") for the recruiter/HR side to match the new
// schema convention. The applicant side remains "applicant".
export const ATS_CHAT_SENT_BY = {
  RECRUITER: "hr",
  APPLICANT: "applicant",
} as const;

// Number of messages loaded per page in the ATS chat thread — used by both
// the initial/recent fetch and the load-older pagination query, and by the
// chat UIs to decide whether a fuller history may exist.
export const ATS_CHAT_PAGE_SIZE = 50;

// Notification channel chosen by the automatic routing logic.
// Push is preferred (instant, free); SMS is the fallback when the applicant
// has no FCM token (no app installed or notifications disabled).
export const ATS_CHAT_NOTIFICATION_CHANNEL = {
  PUSH: "push",
  SMS: "sms",
} as const;

// Title shown on push notifications sent to applicants from ATS chat.
// Kept as a constant so the title is consistent across every call site
// and can be changed in one place.
export const ATS_CHAT_NOTIFICATION_TITLE = "ATS Chat";

// Firebase path segment that separates ATS chat threads from employee chat
// threads. ATS chats live under chats/{orgId}/ats/{applicationId}/, which
// prevents collisions with Dispatch ({employeeId}) and HR
// ({employeeId}-hr) paths under the same chats/{orgId}/ root.
// Named "PREFIX" for historical reasons — it's now a path segment, not a prefix.
export const ATS_CHAT_FIREBASE_PREFIX = "ats";

// Role IDs that should receive ATS chat notifications when an applicant sends a message.
// Covers owner (0), admin (1), HR (2), operations (3), and recruiter (4) — the same
// set used by the existing admin notification route in /api/chat/send-notification-to-admin.
export const ATS_CHAT_NOTIFIED_ROLE_IDS = [0, 1, 2, 3, 4] as const;

// ATS Pipeline Stage Types — the `type` field on atsPipelineStages determines
// which React component renders the stage in the portal and which service
// processes it (form, manual, scheduler, esign, etc.).
// Kept here so both the backend services and the frontend components reference
// a single source of truth instead of hardcoding string literals everywhere.
//
// The descriptions below mirror the schema documentation (schema.ts lines 2212-2222)
// so the constant and the schema stay in sync — if you add a new stage type, update
// both the schema comment and the entry here.
export const ATS_STAGE_TYPES = {
  // Screening questionnaire — the candidate fills out a form via the Form Engine
  // in the portal. The form's knockout rules and scoring determine if they advance.
  FORM: "form",
  // Review Applicants — the recruiter manually reviews the candidate's card and
  // drags it forward or rejects it. No automation, no portal interaction.
  MANUAL: "manual",
  // Interview self-scheduling — the candidate picks an interview slot in the portal
  // from available times configured by the recruiter. Uses the interview slot engine.
  SCHEDULER: "scheduler",
  // Offer signing / document acknowledgement — the offer is sent via Documenso
  // and the candidate signs it in the portal. The webhook updates the stage status.
  ESIGN: "esign",
  // Work email provisioning — the system creates a work email for the candidate
  // via the ImprovMX adapter (e.g. firstname.lastname@linqops.com). ATS waits for
  // confirmation before advancing the candidate to the next stage.
  INTEGRATION_EMAIL: "integration_email",
  // Sexual Harassment Training — the candidate acknowledges and signs a sexual
  // harassment training document via Documenso in the portal. The webhook updates
  // the stage status when the document is signed.
  SEXUAL_HARASSMENT: "sexual_harassment",
  // Amazon DSP onboarding handoff — the candidate's data is handed off to the
  // Amazon DSP Portal via the Chrome extension sync. ATS observes the Amazon
  // onboarding status and mirrors it back. The ATS does NOT initiate this — it
  // reads from the existing Amazon data flow (employee table via employeeId).
  INTEGRATION_AMAZON: "integration_amazon",
  // Background check / drug test — the ATS does NOT initiate these. The status is
  // mirrored from Amazon's onboarding flow (the Chrome extension syncs BGC/drug
  // test results into the employee table). ATS just displays the status and waits.
  // This is why it's called "observed" — we watch, we don't act.
  OBSERVED: "observed",
  // Training — the candidate completes training content blocks and a quiz in the
  // portal via the Form Engine. The quiz score determines if they pass or fail.
  // Uses the same Form Engine as FORM but with content blocks preceding the quiz.
  CONTENT_FORM: "content_form",
  // WOTC (Work Opportunity Tax Credit) — embeds an external provider's iframe
  // in the portal. The candidate fills out the WOTC form inside the iframe, and
  // the external provider handles submission. ATS only tracks completion status.
  IFRAME: "iframe",
  // Hired / Rejected — the end of the pipeline. No further processing happens.
  // The candidate card moves to the Talent Pool (rejected) or is promoted to an
  // employee record (hired). Terminal stages have no portal interaction.
  TERMINAL: "terminal",
} as const;

// ATS Form Field Type Groups — logical groupings of form field types that share
// processing logic. Each group is an array of FormFieldType values that get the
// same treatment in the form engine:
//   - DISPLAY_ONLY  : "section" + "info" — skipped during validation, scoring,
//                     knockout evaluation, and answer enrichment (no answers)
//   - MULTI_CHOICE  : "checkbox" + "checkbox_grid" — store an array of selected
//                     values; share validation logic (every selection must match
//                     an option value) and knockout logic (minSelected / requireWeekend)
// Kept here so all form-engine consumers reference a single source of truth
// instead of hardcoding field type string literals everywhere. Adding a new
// group is just adding a new key here.
export const ATS_FORM_FIELD_TYPE_GROUPS = {
  DISPLAY_ONLY: ["section", "info"],
  MULTI_CHOICE: ["checkbox", "checkbox_grid"],
} as const;

// Stage types where an offer may have been sent — the candidate card fetches
// Stage types where document status is fetched and displayed on the candidate card.
// Includes all stages that can have Documenso documents (esign, sexual_harassment, etc.).
// Adding a new stage type here automatically extends the fetch without touching the component.
export const ATS_OFFER_STATUS_FETCH_STAGE_TYPES: readonly string[] = [
  ATS_STAGE_TYPES.ESIGN,
  ATS_STAGE_TYPES.SEXUAL_HARASSMENT,
  ATS_STAGE_TYPES.TERMINAL,
  ATS_STAGE_TYPES.MANUAL,
] as const;

// Stage types where the "Send Document" button is shown on the candidate card.
// Includes all stages that support sending Documenso documents.
// Each stage type may have different labels (e.g., "Send Offer" for esign,
// "Send Document" for sexual_harassment), but the underlying workflow is identical.
// NOTE: sexual_harassment documents are auto-generated from the submitted quiz
// (no recruiter send action) — only esign offers are recruiter-sent.
export const ATS_SEND_OFFER_STAGE_TYPES: readonly string[] = [
  ATS_STAGE_TYPES.ESIGN,
] as const;

// ATS Interview Slot — status values for atsInterviewSlots.status
// "open" = applicants can book | "full" = capacity reached | "closed" = recruiter closed it | "cancelled" = interviewer unavailable
export const ATS_INTERVIEW_SLOT_STATUS = {
  OPEN: "open",
  FULL: "full",
  CLOSED: "closed",
  CANCELLED: "cancelled",
} as const;

// ATS Interview Mode — determines which instructions the applicant receives
export const ATS_INTERVIEW_MODE = {
  IN_PERSON: "in_person",
  PHONE: "phone",
  VIDEO: "video",
} as const;

// ATS Interview Mode dropdown options — used by SlotModal for both one-time and
// recurring modes so the mode list is defined in one place, not duplicated
export const ATS_INTERVIEW_MODE_OPTIONS = [
  { label: "In Person", value: ATS_INTERVIEW_MODE.IN_PERSON },
  { label: "Phone", value: ATS_INTERVIEW_MODE.PHONE },
  { label: "Video", value: ATS_INTERVIEW_MODE.VIDEO },
];

// ATS Interview Booking Status — values for bookingStatus in atsStageInstances.data
// "booked" = candidate booked a slot | "completed" = interview done | "no_show" = candidate didn't show | "cancelled" = recruiter cancelled | "rescheduled" = moved to a new slot
export const ATS_INTERVIEW_BOOKING_STATUS = {
  BOOKED: "booked",
  COMPLETED: "completed",
  NO_SHOW: "no_show",
  CANCELLED: "cancelled",
  RESCHEDULED: "rescheduled",
} as const;

// ATS Interview Booking action options — used by the DropdownsOne in the Bookings tab
// Only these three actions are available; "booked" is the initial state, not an action
export const ATS_INTERVIEW_BOOKING_STATUS_ACTIONS = [
  { label: "Mark Completed", value: ATS_INTERVIEW_BOOKING_STATUS.COMPLETED },
  { label: "Mark No Show", value: ATS_INTERVIEW_BOOKING_STATUS.NO_SHOW },
  { label: "Cancel Booking", value: ATS_INTERVIEW_BOOKING_STATUS.CANCELLED },
];

// ATS Interview Slot Template — the `type` value in atsStageConfigs for recurring slot templates
export const ATS_INTERVIEW_SLOT_TEMPLATE_TYPE = "interview_slot_template";

// ATS Interview Slot Type — distinguishes one-time slots from recurring-generated slots
// Used by the Type column badges in the Slots tab
export const ATS_INTERVIEW_SLOT_TYPE = {
  ONE_TIME: "one_time",
  RECURRING: "recurring",
} as const;

// ATS Interview Slot Type display labels — used by the Type column in the Slots tab
// Maps the slot type values to human-readable labels for the badge display
export const ATS_INTERVIEW_SLOT_TYPE_LABELS: Record<string, string> = {
  [ATS_INTERVIEW_SLOT_TYPE.ONE_TIME]: "One-time",
  [ATS_INTERVIEW_SLOT_TYPE.RECURRING]: "Recurring",
};

// ATS Interview Slot Type badge styles — used by the Type column in the Slots tab
// Blue for recurring (stands out), gray for one-time (neutral)
export const ATS_INTERVIEW_SLOT_TYPE_STYLES: Record<string, string> = {
  [ATS_INTERVIEW_SLOT_TYPE.ONE_TIME]: "bg-gray-100 text-gray-700",
  [ATS_INTERVIEW_SLOT_TYPE.RECURRING]: "bg-blue-50 text-blue-700",
};

// Day-of-week short labels for UI display — index matches JS Date.getDay() (0=Sunday)
// Used by the SlotModal recurring pattern form (checkbox grid for selecting days)
export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

// ATS Interview Slot — status badge styles for the Slots settings tab
export const ATS_INTERVIEW_SLOT_STATUS_STYLES: Record<string, string> = {
  [ATS_INTERVIEW_SLOT_STATUS.OPEN]: "bg-green-50 text-green-700",
  [ATS_INTERVIEW_SLOT_STATUS.FULL]: "bg-blue-50 text-blue-700",
  [ATS_INTERVIEW_SLOT_STATUS.CLOSED]: "bg-gray-100 text-gray-700",
  [ATS_INTERVIEW_SLOT_STATUS.CANCELLED]: "bg-red-50 text-red-700",
};

// ATS Interview Booking — status badge styles for the Bookings settings tab
export const ATS_INTERVIEW_BOOKING_STATUS_STYLES: Record<string, string> = {
  [ATS_INTERVIEW_BOOKING_STATUS.BOOKED]: "bg-blue-50 text-blue-700",
  [ATS_INTERVIEW_BOOKING_STATUS.COMPLETED]: "bg-green-50 text-green-700",
  [ATS_INTERVIEW_BOOKING_STATUS.NO_SHOW]: "bg-yellow-50 text-yellow-700",
  [ATS_INTERVIEW_BOOKING_STATUS.CANCELLED]: "bg-red-50 text-red-700",
  [ATS_INTERVIEW_BOOKING_STATUS.RESCHEDULED]: "bg-purple-50 text-purple-700",
};

// ATS Stage Instance Status — values for atsStageInstances.status
// "in_progress" = candidate is currently in this stage | "completed" = stage finished, application moved on
export const ATS_STAGE_INSTANCE_STATUS = {
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
} as const;
// -----------------------------------------------------------------------------
// ATS Stage Type — Display Metadata (labels, badge colors, dropdown options)
// -----------------------------------------------------------------------------
// These three constants provide the UI-facing metadata for each stage type.
// They are kept in constants.ts (not in the component files) so that any UI
// component that needs to display stage type information — the settings list,
// the StageModal dropdown, the Pipeline Board column headers, the Candidate
// Drawer — can import from a single source of truth. If a new stage type is
// added to ATS_STAGE_TYPES, add its entry to all three below.
// -----------------------------------------------------------------------------

// Tailwind color combinations for the stage type badge — gives each type a
// consistent visual identity on the board and in the settings list.
// Used by StageList.tsx and (future) Pipeline Board column headers.
export const ATS_STAGE_TYPE_BADGE_STYLES: Record<string, string> = {
  form: "bg-blue-50 text-blue-700",
  manual: "bg-gray-100 text-gray-700",
  scheduler: "bg-purple-50 text-purple-700",
  esign: "bg-green-50 text-green-700",
  integration_email: "bg-indigo-50 text-indigo-700",
  integration_amazon: "bg-orange-50 text-orange-700",
  observed: "bg-yellow-50 text-yellow-700",
  content_form: "bg-teal-50 text-teal-700",
  iframe: "bg-pink-50 text-pink-700",
  terminal: "bg-red-50 text-red-700",
};

// Short human-readable labels for compact badges (e.g. on the stage list row).
// These are shorter than the dropdown labels below — they fit in a small badge.
export const ATS_STAGE_TYPE_SHORT_LABELS: Record<string, string> = {
  form: "Form",
  manual: "Manual",
  scheduler: "Scheduler",
  esign: "E-sign",
  integration_email: "Email",
  integration_amazon: "Amazon",
  observed: "Observed",
  content_form: "Training",
  iframe: "iframe",
  terminal: "Terminal",
};

// Full human-readable labels for the stage type dropdown in the StageModal.
// The raw value (e.g. "integration_email") is sent to the API; the label
// (e.g. "Work Email Provisioning") is what the recruiter sees.
export const ATS_STAGE_TYPE_DROPDOWN_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  { value: ATS_STAGE_TYPES.FORM, label: "Screening Questionnaire (Form)" },
  { value: ATS_STAGE_TYPES.MANUAL, label: "Manual Review" },
  { value: ATS_STAGE_TYPES.SCHEDULER, label: "Interview Scheduler" },
  { value: ATS_STAGE_TYPES.ESIGN, label: "Offer / E-Sign" },
  { value: ATS_STAGE_TYPES.INTEGRATION_EMAIL, label: "Work Email Provisioning" },
  { value: ATS_STAGE_TYPES.SEXUAL_HARASSMENT, label: "Sexual Harassment" },
  { value: ATS_STAGE_TYPES.INTEGRATION_AMAZON, label: "Amazon Onboarding" },
  // TODO: Implement these stages
  // {
  //   value: ATS_STAGE_TYPES.OBSERVED,
  //   label: "Background Check / Drug Test (Observed)",
  // },
  // { value: ATS_STAGE_TYPES.CONTENT_FORM, label: "Training Content + Quiz" },
  // { value: ATS_STAGE_TYPES.IFRAME, label: "WOTC (External iframe)" },
  { value: ATS_STAGE_TYPES.TERMINAL, label: "Hired" },
];

// ATS Questionnaire Stages — the `stage` value on atsStageConfigs rows where
// type="form_schema". A form's stage matches the pipeline stage type it can be
// bound to (form.stage = "form" binds to stage.type = "form"), so stage-binding
// dropdowns only offer questionnaires built for that stage.
// Kept aligned with ATS_STAGE_TYPES — add an entry when a new stage type
// supports questionnaires.
export const ATS_FORM_STAGE = {
  SCREENING: ATS_STAGE_TYPES.FORM,
  SEXUAL_HARASSMENT: ATS_STAGE_TYPES.SEXUAL_HARASSMENT,
} as const;

// Stage options shown in the questionnaire setup (FormBuilderModal). The label
// is what the recruiter sees; the value is stored in atsStageConfigs.stage.
export const ATS_FORM_STAGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: ATS_FORM_STAGE.SCREENING, label: "Screening" },
  { value: ATS_FORM_STAGE.SEXUAL_HARASSMENT, label: "Sexual Harassment" },
];

// Display labels for the stage badge in the questionnaires list. NULL/legacy
// rows are treated as screening (they predate the stage column).
export const ATS_FORM_STAGE_LABELS: Record<string, string> = {
  [ATS_FORM_STAGE.SCREENING]: "Screening",
  [ATS_FORM_STAGE.SEXUAL_HARASSMENT]: "Sexual Harassment",
};

// Max answerable questions per questionnaire (display-only section/info fields
// don't count). Enforced client-side in FormBuilderModal + server-side in
// createFormSchemaValidator.
export const ATS_FORM_MAX_QUESTIONS = 25;

// atsFormResponses.status values — a response only counts toward "form
// submitted" once it reaches SUBMITTED (quiz-retry attempts on quiz stages
// are never persisted, so this gate is what keeps them invisible).
export const ATS_FORM_RESPONSE_STATUS = {
  SUBMITTED: "submitted",
} as const;

// =============================================================================
// ATS Portal File Upload — config for candidate file-upload questions
// =============================================================================
// Used by POST /api/portal/[token]/upload-file (presign) and by
// verifyPortalFileAnswers() in atsFormService (submit-time verification).
// The portal surface is public (magic-link auth), so these limits are the
// security boundary — single source of truth, no magic strings in routes.
// =============================================================================
export const ATS_PORTAL_FILE_UPLOAD = {
  // S3 key segment used as generateS3FileName's "source" — keys look like
  // {orgId}/ats-portal/{applicationId}/{YYYYMMDD}/{fieldId}-{rand}/{fileName}
  SOURCE: "ats-portal",
  // Mime allowlist — matches DocumentsUploadModal's accept list (.png/.jpg/.pdf).
  // ContentType is baked into the PUT signature, so this check is the real gate.
  ALLOWED_MIME_TYPES: [
    "application/pdf",
    "image/jpeg",
    "image/png",
  ],
  // 10 MB — same cap as DocumentsUploadModal's MAX_TOTAL_UPLOAD_BYTES
  MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024,
  // Presigned PUT expiry — same 5 minutes as the existing /api/upload-file presign
  PRESIGN_EXPIRY_SECONDS: 300,
  // Presign requests per IP per window — public endpoint flood protection
  RATE_LIMIT_PER_IP: 10,
  RATE_LIMIT_WINDOW_MS: 60 * 1000,
  // Presign requests per TOKEN per window — a second, independent cap. Per-IP
  // limits can be bypassed by rotating IPs (botnets, VPNs); a per-token cap
  // follows the credential itself, bounding the damage of a single leaked
  // link. A form has few file fields, so 10/day is generous for real use.
  RATE_LIMIT_PER_TOKEN: 10,
  RATE_LIMIT_WINDOW_MS_PER_TOKEN: 24 * 60 * 60 * 1000,
} as const;

// =============================================================================
// Forward Email Configuration — Work Email Provisioning (Stage 5)
// =============================================================================
// Configuration for Forward Email API integration used in Stage 5 (Work Email
// Provisioning). These constants define the provider, domain, and API endpoint.
// The API token is stored in secrets.env and accessed via SST resources.
// =============================================================================

export const FORWARD_EMAIL_CONFIG = {
  PROVIDER: "forwardemail",
  API_BASE_URL: "https://api.forwardemail.net",
} as const;

// Work Email Provisioning Status — values for stage_instance.data.status
// "provisioning" = alias creation in progress
// "active" = alias created and ready to use
// "failed" = alias creation failed (see failureReason for details)
// "released" = alias was released (candidate rejected/hired, but alias persists)
export const WORK_EMAIL_STATUS = {
  PROVISIONING: "provisioning",
  ACTIVE: "active",
  FAILED: "failed",
  RELEASED: "released",
} as const;

// Offer recipient roles — stored on atsStageInstances.data.recipients[].role when an offer
// is sent, and read back to tell the candidate's signature apart from a manager's.
// Distinct from ATS_HISTORY_ACTOR_TYPE.APPLICANT and ATS_CHAT_SENT_BY.APPLICANT, which share
// the same string but describe an audit actor and a chat sender respectively.
// Manager recipients are numbered at send time ("manager-1", "manager-2", ...), so only the
// applicant role is a fixed value.
export const ATS_OFFER_RECIPIENT_ROLE = {
  APPLICANT: "applicant",
} as const;

// Work Email Status badge — label + classes for the candidate drawer, same shape as ATS_OFFER_STATUS_STYLES
export const WORK_EMAIL_STATUS_STYLES: Record<string, { label: string; class: string }> = {
  [WORK_EMAIL_STATUS.PROVISIONING]: { label: "Provisioning…", class: "bg-yellow-50 text-yellow-700" },
  [WORK_EMAIL_STATUS.ACTIVE]: { label: "Active", class: "bg-green-50 text-green-700" },
  [WORK_EMAIL_STATUS.FAILED]: { label: "Failed", class: "bg-red-50 text-red-700" },
  [WORK_EMAIL_STATUS.RELEASED]: { label: "Released", class: "bg-gray-100 text-gray-600" },
};

// Work Email — pipeline card badge for the one state a recruiter must act on. Provisioning
// is transient and resolves on its own, and a working email needs no badge, so only a
// failure is surfaced on the board. The drawer shows every state via WORK_EMAIL_STATUS_STYLES.
export const WORK_EMAIL_CARD_FAILED_STYLE = {
  label: "Work Email Creation Failed",
  class: "bg-red-50 text-red-700",
};

// Work Email Trigger — what started a provisioning attempt, stored in stage_instance.data.workEmail.triggeredBy
// "offer_signed" = candidate signed their offer letter (portal or Documenso webhook)
// "manual"       = recruiter clicked Provision/Retry in the candidate drawer
export const WORK_EMAIL_TRIGGER = {
  OFFER_SIGNED: "offer_signed",
  MANUAL: "manual",
} as const;

// Soft-lock window for work email provisioning. While an attempt is marked "provisioning"
// and its lastAttemptAt is newer than this, other callers skip — prevents two paths that
// fire within milliseconds (portal + webhook) from both creating a Forward Email alias.
// Older than this = the attempt is assumed to have crashed, and a new one may proceed.
export const WORK_EMAIL_PROVISIONING_LOCK_MS = 60_000;
// Work Email Error Codes — categorizes failures for recruiter notifications
// Used to provide human-readable error messages to recruiters
export const WORK_EMAIL_ERROR_CODES = {
  API_AUTH_FAILED: "api_auth_failed",
  ALIAS_COLLISION: "alias_collision",
  DOMAIN_NOT_FOUND: "domain_not_found",
  INVALID_RECIPIENT: "invalid_recipient",
  API_RATE_LIMIT: "api_rate_limit",
  API_SERVER_ERROR: "api_server_error",
  DNS_VERIFICATION_FAILED: "dns_verification_failed",
} as const;

// Work Email Error Messages — human-readable messages for recruiters
// Maps error codes to actionable messages displayed in the UI
export const WORK_EMAIL_ERROR_MESSAGES: Record<string, string> = {
  [WORK_EMAIL_ERROR_CODES.API_AUTH_FAILED]:
    "Failed to authenticate with Forward Email service. Check API token configuration.",
  [WORK_EMAIL_ERROR_CODES.ALIAS_COLLISION]:
    "Could not create unique email alias after 10 attempts. Manual intervention needed.",
  [WORK_EMAIL_ERROR_CODES.DOMAIN_NOT_FOUND]:
    "Domain linqdsp.com not found in Forward Email account.",
  [WORK_EMAIL_ERROR_CODES.INVALID_RECIPIENT]:
    "Personal email address is invalid. Check applicant record.",
  [WORK_EMAIL_ERROR_CODES.API_RATE_LIMIT]:
    "Forward Email API rate limit exceeded. Retry in 5 minutes.",
  [WORK_EMAIL_ERROR_CODES.API_SERVER_ERROR]:
    "Forward Email service error. Retry later.",
  [WORK_EMAIL_ERROR_CODES.DNS_VERIFICATION_FAILED]:
    "Domain DNS records not properly configured.",
} as const;

// Email Providers — supported email forwarding providers
// Used in EmailNamingRule.provider to specify which service handles forwarding
export const WORK_EMAIL_PROVIDERS = {
  FORWARDEMAIL: "forwardemail",
  IMPROVMX: "improvmx",
  CLOUDFLARE: "cloudflare",
  GOOGLE: "google",
  M365: "m365",
  MANUAL: "manual",
} as const;

// Email Collision Strategies — how to handle duplicate alias names
// Used in EmailNamingRule.collisionStrategy to determine suffix generation
export const WORK_EMAIL_COLLISION_STRATEGIES = {
  NUMERIC_SUFFIX: "numeric_suffix",
  RANDOM_SUFFIX: "random_suffix",
} as const;

export const COACHING_CATEGORIES = {
  QUALITY: {
    key:"Quality",
    subCategories: { 
      CDF: {
        key:'CDF',
        infractionTypes:{
          MISHANDLED_PACKAGE:{
            key:'Mishandled package',
          },
          UNPROFESSIONAL:{
            key:'Unprofessional',
          },
          NOT_FOLLOW_INSTRUCTION:{
            key:'Not follow Instruction',
          },
          WRONG_ADDRESS:{
            key:'Wrong Address',
          },
          NEVER_RECEIVED:{
            key:'Never Received',
          },
          WRONG_ITEM:{
            key:'Wrong Item',
          }
        }
      },
      DSB: {
        key:'DSB',
        infractionTypes:{
          SIMULTANEOUS_DELIVERIES:{
            key:'Simultaneous Deliveries',
          },
          DELIVERIES_50_METERS:{
            key:'Deliveries > 50 Meters',
          },
          INCORRECT_SCAN_USAGE_AD:{
            key:'Incorrect Scan Usage – AD',
          },
          INCORRECT_SCAN_USAGE_UD:{
            key:'Incorrect Scan Usage – UD',
          },
          NO_POD_DELIVERY:{
            key:'No POD on Delivery',
          },
          SNDNR:{
            key:'SNDNR',
          }
        }
      },
      DCR: {
        key:'DCR',
        infractionTypes:{
          CONTACT_COMPLIANT:{
            key:'Contact Compliant',
            acceptedValues: [
              'ADDRESS NOT FOUND',
              'BAD WEATHER',
              'CUSTOMER UNAVAILABLE',
              'INACCESSIBLE DELIVERY LOCATION',
              'NO SECURE LOCATION',
              'UNSAFE DUE TO DOG'
            ]
          },
          OODT: {
            key: 'OODT',
            acceptedValues: ['OODT', 'OUT OF DRIVING TIME']
          },
          RTS_OTHER: {
            key: 'No RTS Code Selected',
            acceptedValues: ['RTS - OTHER', 'NO RTS CODE SELECTED']
          },
          BUSINESS_CLOSED: {
            key: 'Business Closed',
            acceptedValues: ['BUSINESS CLOSED']
          }
        }
      },
      Fleet: {
        key:'Fleet',
        infractionTypes:{
          DVIC:{
            key:'DVIC',
          }
        }
      }
    }
  },
  SAFETY: {
    key:"Safety",
    subCategories: {
      Netradyne: {
        key:'Netradyne',
        infractionTypes:{
          HIGH_G:{
            key:'High G',
          },
          LOW_IMPACT:{
            key:'Low Impact',
          },
          POTENTIAL_COLLISION:{
            key:'Potential Collision',
          },
          BACKING:{
            key:'Backing',
          },
          SIGN_VIOLATIONS:{
            key:'Sign Violations',
          },
          TRAFFIC_LIGHT_VIOLATIONS:{
            key:'Traffic Light Violations',
          },
          DRIVER_DISTRACTION:{
            key:'Driver Distraction',
          },
          FOLLOWING_DISTANCE:{
            key:'Following Distance',
          },
          SPEEDING_VIOLATIONS:{
            key:'Speeding Violations',
          },
          SEATBELT_COMPLIANCE:{
            key:'Seatbelt Compliance',
          },
          U_TURN:{
            key:'U Turn',
          },
          HARD_BRAKING:{
            key:'Hard Braking',
          },
          HARD_TURN:{
            key:'Hard Turn',
          },
          HARD_ACCELERATION:{
            key:'Hard Acceleration',
          },
          DRIVER_DROWSINESS:{
            key:'Driver Drowsiness',
          },
          WEAVING:{
            key:'Weaving',
          },
          CAMERA_OBSTRUCTION:{
            key:'Camera Obstruction',
          }
        }
      },
      Geotab: {
        key:'Geotab',
        infractionTypes:{
          SEATBELT:{
            key:'Seatbelt',
          },
          SPEEDING:{
            key:'Speeding',
          }
        }
      },
      eMentor: {
        key:'eMentor',
        infractionTypes:{
          FICO_SCORE:{
            key:'FICO Score',
          },
          BRAKING:{
            key:'Braking',
          },
          IDLING:{
            key:'Idling',
          },
          CORNERING:{
            key:'Cornering',
          },
          DISTRACTION:{
            key:'Distraction',
          },
          BACKUP:{
            key:'BackUp',
          },
          SPEEDING:{
            key:'Speeding',
          },
          ACCELERATION:{
            key:'Acceleration',
          },
          EOC:{
            key:'EOC',
          },
          MENTOR_TRAINING_VIDEOS:{
            key:'Mentor Training Videos',
          },
          SPEEDING_EVENT_RATE:{
            key:'Speeding Event Rate',
          },
          SEATBELT_OFF_RATE:{
            key:'Seatbelt Off Rate',
          }
        }
      }
    }
  },
  DSP_SPECIFIC: {
    key:"DSP Specific",
    subCategories: {
      Attendance: {
        key:'Attendance',
        infractionTypes:{
          TARDINESS:{
            key:'Tardiness (Late Arrival)',
          },
          LATE_SCHEDULE_CHANGE_REQUEST:{
            key:'Late Schedule Change Request',
          },
          LATE_TIME_OFF_REQUEST:{
            key:'Late Time-off Request',
          },
          WEEKEND_OFF_REQUEST:{
            key:'Weekend Off Request',
          },
          CALL_OUT:{
            key:'Call Out',
          },
          NO_SHOW:{
            key:'No Show',
          }
        }
      },
      Behavior: {
        key:'Behavior',
        infractionTypes:{
          MEAL_BREAK:{
            key:'Meal Break',
          },
          GAS_REFILL:{
            key:'Gas Refill',
          },
          MISS_PUNCH:{
            key:'Miss Punch (In/Out or Break)',
          },
          TRASH_IN_VAN:{
            key:'Trash in Van',
          },
          DVIC:{
            key:'DVIC',
          },
          MENTOR_PRE_POST_INSPECTION:{
            key:'Mentor Pre-Post Inspection',
          },
          MISSING_PACKAGES_START_ROUTE:{
            key:'Missing Packages at Start of Route',
          }
        }
      },
      Performance: {
        key:'Performance',
        infractionTypes:{
          PERFORMANCE_ROUTE:{
            key:'Performance of Route',
          },
          EMENTOR:{
            key:'eMentor',
          },
          UNPLANNED_STOPS:{
            key:'Unplanned Stops',
          },
          TIME_OF_TASK_TIME_THEFT:{
            key:'Time Of Task/Time Theft',
          },
          ACCIDENTS:{
            key:'Accidents',
          },
          STUCK_IN_MUD:{
            key:'Stuck in Mud',
          }
        }
      }
    }
  }
} as const

// Database batch operation size to avoid NeonDB parameter size limits
export const DATABASE_BATCH_SIZE = 300;

// Documenso download version constants
export const DOCUMENSO_VERSION = {
  PENDING: "pending",
  SIGNED: "signed",
} as const;

// Incident Status Constants
export const INCIDENT_STATUS = {
  OPEN: "Open",
  CLOSED: "Closed",
  ALL: "All",
} as const;

// Incident Question Bank Constants
export const INCIDENT_TYPES = {
  INCIDENT: "incident",
  ACCIDENT: "accident", 
  INJURY: "injury"
} as const;

export const INCIDENT_UPLOAD_TYPES = {
  DOCUMENTS: "documents",
  VIDEOS: "videos",
  DOCTORS_NOTE: "doctorsNote",
} as const;

export type IncidentUploadType =
  typeof INCIDENT_UPLOAD_TYPES[keyof typeof INCIDENT_UPLOAD_TYPES];

// Admin Webhook Key Mapping
// Maps trigger names to database keys for admin-level webhooks
//
// Why this mapping exists:
// - Admin webhooks store URLs in the admin table's webhook JSONB column
// - Trigger names (e.g., 'adminSignupWebhook') don't always match database keys (e.g., 'signup')
// - Centralizes configuration and makes it easy to add new admin webhooks
export const ADMIN_WEBHOOK_KEY_MAP: Record<string, string> = {
  adminSignupWebhook: 'signup',
  subscriptionCancelRequest: 'subscriptionCancelRequest',
  subscriptionCancelled: 'subscriptionCancelled',
  subscriptionPlanChanged: 'subscriptionPlanChanged'
} as const;

export const INCIDENT_UPLOAD_TYPE_LABELS: Record<IncidentUploadType, string> = {
  [INCIDENT_UPLOAD_TYPES.DOCUMENTS]: "documents",
  [INCIDENT_UPLOAD_TYPES.VIDEOS]: "videos",
  [INCIDENT_UPLOAD_TYPES.DOCTORS_NOTE]: "doctor's note",
} as const;

// Help Desk Ticket Types
// Used for user-created support requests (Help → Contact Support page).
export const HELP_DESK_TICKET_TYPES = {
  BILLING: "billing",
  BUG: "bug",
  FEATURE_REQUEST: "feature_request",
  ACCOUNT_ACCESS: "account_access",
  INTEGRATION: "integration",
  OTHER: "other",
} as const;

export const HELP_DESK_TICKET_TYPE_OPTIONS = Object.values(
  HELP_DESK_TICKET_TYPES,
) as readonly string[];

export const USAGE_LOCATIONS = {
  INCIDENT_DETAIL: "incident_detail",
  MANAGER_REPORT: "manager_report",
  DRIVER_RESPONSE: "driver_response"
} as const;

// EOD Summary Sync Sources
// --------------------------------------------------------------------------
export const EOD_SOURCES = {
  NETRADYNE: 'netradyne',
  INCIDENT: 'incident',
  CORRECTIVE_ACTION: 'correctiveaction',
  ROUTE_OVERVIEW: 'routeoverview',
  TOMORROWS_DISPATCHERS: 'tomorrowsdispatchers',
} as const;

export const ANSWER_TYPES = {
  BOOLEAN: "boolean",
  TEXT: "text",
  TEXTAREA: "textarea",
  SELECT: "select",
  DATE: "date",
  NUMBER: "number"
} as const;

// Corrective Action Constants
export const CORRECTIVE_ACTION_SOURCES = {
  CATEGORY: "category",
  SUB_CATEGORY: "subCategory",
  INFRACTION_TYPE: "infractionType"
} as const;

export const CORRECTIVE_ACTION_SOURCE_OPTIONS = Object.values(CORRECTIVE_ACTION_SOURCES);

export const CORRECTIVE_ACTION_WARNING_LEVELS = {
  FIRST_WARNING: {
    key: "first_warning",
    label: "First Warning"
  },
  SECOND_WARNING: {
    key: "second_warning",
    label: "Second Warning"
  },
  THIRD_WARNING: {
    key: "third_warning",
    label: "Third Warning"
  },
  FINAL_WARNING: {
    key: "final_warning",
    label: "Final Warning"
  },
  SUSPENSION: {
    key: "suspension",
    label: "Suspension"
  },
  TERMINATION: {
    key: "termination",
    label: "Termination"
  }
} as const;

export const CORRECTIVE_ACTION_WARNING_LEVEL_OPTIONS = Object.values(CORRECTIVE_ACTION_WARNING_LEVELS);

// Compliance Rule Display Name Templates
// Used for dynamic rule name generation based on effective values
export const DISPLAY_NAME_TEMPLATES: Record<string, string> = {
  // Weekly work hours limit
  weeklyWorkHoursCheck: 'Exceeded weekly limit of {greaterThan} hours',

  // Last delivery clock out grace period
  lastDeliveryClockOutDiffCheck: 'Last Delivery Clock Out Time Difference - Grace {rtsGracePeriodMinutes} mins',

  // Clock in/out display grace period
  clockInOutDisplayRule: '{gracePeriodMinutes} mins grace period for clock in/out display',

  // Clock in time check (consolidated lunch break rule)
  clockInTimeCheck: 'Lunch break between {lunchBreakWindowStartOffsetMinutes} mins from start {lunchBreakWindowEndTimePart} and minimum {minLunchBreakMinutes} mins lunch break{clockInCondition} and > {minWorkHours} hrs work',

  // Display clock in time difference
  displayClockInTimeDifferenceCheck: 'Display Clock In Time Difference - Grace {gracePeriodMinutes} mins',

  // Display clock out time difference
  displayClockOutTimeDifferenceCheck: 'Display Clock Out Time Difference - Grace {gracePeriodMinutes} mins',

  // Minimum lunch break
  minimumLunchBreakCheck: 'Minimum {minLunchBreakMinutes} mins lunch break for > {minWorkHours} hrs work',

  // Weekly work hours range (50-60 hours)
  weeklyWorkHoursRangeCheck: 'Worked {min}-{max} hours in past 7 days',

  // Weekly consecutive days check
  weeklyConsecutiveDaysCheck: 'Exceeded weekly limit of {maxConsecutiveDays} consecutive days',
  
  // Excess break time
  excessBreakTime: 'Total Break Time Exceeds {maxPaidBreakMinutes} Minutes of Paid Break',

  // Pay Class FLSA Check — dynamic display name using configured payClass and FLSA values
  // WHY: Shows the actual configured values in the violation name so users see what was flagged (e.g., "Hourly employee marked as FLSA Exempt")
  payClassFLSACheck: '{payClass} employee marked as FLSA {FLSA}',
};

export const CORRECTIVE_ACTION_UNITS = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month"
} as const;

export const CORRECTIVE_ACTION_STATUS = {
  OPEN: {
    key: "open",
    label: "Open"
  },
  CLOSED: {
    key: "closed",
    label: "Closed"
  },
  DELETED: {
    key: "deleted",
    label: "Deleted"
  }
} as const;

export const CORRECTIVE_ACTION_UNIT_OPTIONS = Object.values(CORRECTIVE_ACTION_UNITS);

/**
 * Default per-service-area schedule settings used when creating a new organization.
 * Mirrors the previous implicit DB defaults of the deprecated scalar columns
 * (defaultStandupTime="45", singleStandup=true, backupClockIn="1") so that new
 * orgs satisfy the schedule-settings page validator on first visit.
 */
export const SCHEDULE_SETTING_DEFAULTS = {
  defaultStandupTimeMinutes: "45",
  isSingleStandup: true,
  backupClockInWave: "1",
} as const;

// Dispatch Notification Status Constants
export const DISPATCH_NOTIFICATION_STATUS = {
  SENT: 'sent',
  FAILED: 'failed',
  NOT_SENT: 'not_sent',
  PENDING: 'pending',
} as const;

export type DispatchNotificationStatus = typeof DISPATCH_NOTIFICATION_STATUS[keyof typeof DISPATCH_NOTIFICATION_STATUS];

// Qualification labels mapping for dispatch page
// Centralized configuration for employee qualification labels shown on dispatch page
// Defines regex patterns to match qualification strings from database and display names for UI
// This allows easy addition/removal of qualification labels without changing backend/frontend logic
export const QUALIFICATION_LABELS = {
  CDV: {
    pattern: /cdv/,
    display: 'CDV',
  },
  STEP_VAN: {
    pattern: /step|van/,
    display: 'Step Van',
  },
  DOT: {
    pattern: /dot/,
    exclude: /non-dot/, // Exclude NON-DOT to avoid false positives
    display: 'DOT',
  },
} as const;

// Application Identity Constants
export const APP_NAMES = {
  MAIN_APP: 'mainApp',
  ADMIN_APP: 'adminApp',
} as const;

// Application Role Range Restrictions
// Determines which roles are allowed to access which application
export const APP_ALLOWED_ROLE_RANGES = {
  [APP_NAMES.MAIN_APP]: { min: ROLES.EXPERIMENTAL_OWNER, max: ROLES.APPLICANT },    // Customer roles (0 to 11)
  [APP_NAMES.ADMIN_APP]: { min: ROLE_RANGES.PROVIDER_LINQOPS_SUPPORT.MIN, max: ROLE_RANGES.PROVIDER_LINQOPS_SUPPORT.MAX }, // Admin roles (200 to 210)
} as const;

// DOT vehicle service types for DVIC violation detection
// Used to classify vehicles as DOT or Non-DOT for inspection time thresholds
// Case-insensitive matching against fleet.serviceType
export const DOT_SERVICE_TYPES = [
  "Step Van",
] as const;

// DVIC violation thresholds in seconds
// DOT vehicles: 300 seconds (5 minutes)
// Non-DOT vehicles: 90 seconds (1.5 minutes)
export const DVIC_THRESHOLDS = {
  DOT: 300,
  NON_DOT: 90,
} as const;

// Tomorrow Dispatch Warning Messages
export const TOMORROW_DISPATCH_WARNINGS = {
  NO_EMPLOYEES_SCHEDULED: 'Warning: No employees are scheduled for tomorrow.',
  NO_ROSTER_INFO: 'Warning: We do not have roster data available for drivers for tomorrow.',
  NO_ROUTE_TARGET: 'Warning: We do not have route target data for tomorrow.',
} as const;

// ──────────────────────────────────────────────────────────────────
// Chrome Extension Sync Directives
// ──────────────────────────────────────────────────────────────────
// Hardcoded sync directive arrays sent to the Chrome extension via the sync-status API.
// These are the canonical source-of-truth configurations for:
//   - TRACK_URLS        : passive interception (AWS API → LinqOps backend)
//   - CONDITIONAL_TRACK_URLS : active polling triggered after page load
// Keep these in sync with the arrays defined in inject.ts.
// In the future these can be moved to the database for live updates without extension releases.

// Passive tracking URL configurations (AWS API → LinqOps backend forwarding)
// Each entry specifies which Amazon API endpoint to intercept and where to forward the data
export const EXTENSION_SYNC_TRACK_URLS: TrackUrlConfig[] = [
  { awsUrl: "/scheduling/home/api/v2/rosters", serverUrl: "/api/amazon/scheduling/home/v2/rosters", skipQueryParams: false },
  { awsUrl: "/schedulingV2/home/api/v4/rosters", serverUrl: "/api/amazon/schedulingV2/home/v4/rosters", skipQueryParams: false },
  { awsUrl: "/fleet-management/api/vehicles", serverUrl: "/api/amazon/fleet-management/vehicles", skipQueryParams: false },
  { awsUrl: "/workforce/api/v1/fetchDSPAssociates", serverUrl: "/api/amazon/workforce/v1/fetchDSPAssociates", skipQueryParams: false },
  { awsUrl: "/dsp-account-management/data/get-da-onboarding-data", serverUrl: "/api/amazon/v2/dsp-account-management/data/get-da-onboarding-data", skipQueryParams: false },
  {
    awsUrl: "/operations/execution/api/summaries",
    serverUrl: "/api/amazon/operations/execution/summaries",
    skipQueryParams: false,
    keys: {
      routeSummaries: true,
      itinerarySummaries: [
        "transporterId",
        "vinNumber",
        "executionStatus",
        "isScheduledDeliveryAtRisk",
        "routeCode",
        "driverSessionEnded",
        "progressStatus",
        "sessionEndTime",
        "breaks",
        "routePauseEvents",
        "lastStopExecutionTime",
      ],
      transporters: true
    }
  },
  {
    awsUrl: "/operations/execution/api/route-summaries",
    serverUrl: "/api/amazon/operations/execution/route-summaries",
    skipQueryParams: false,
    keys: {
      rmsRouteSummaries: [
        "routeCode", 
        "isScheduledDeliveryAtRisk", 
        "routeStatus", 
        "lateDeparting", 
        "swaroute", 
        "sessionEndTime", 
        "progressStatus", 
        "customerReturnAttributes", 
        { 
          transporters: [
            "breaks",  
            "routePauseEvents", 
            "routePauseStatus", 
            "projectedCompletionTime", 
            "itineraryStatus", 
            "driverSessionEnded"
          ],
          routeDeliveryProgress:true
        }
      ],
      transporters: true
    }
  },
  {
    awsUrl: "/operations/execution/api/routes/summary",
    serverUrl: "/api/amazon/operations/execution/routes/summary",
    skipQueryParams: false,
    keys: {
      routeSummaries: ["routeCode", "transporterIdList", "routeExecutionStatus"]
    }
  },
  { awsUrl: "/fleet-management/api/afs-requests", serverUrl: "/api/amazon/fleet-management/afs-requests", skipQueryParams: false },
  { awsUrl: "/scheduling/home/api/v2/assign-work", serverUrl: "/api/amazon/scheduling/home/v2/assign-work", skipQueryParams: true },
  { awsUrl: "/scheduling/home/api/v2/unassign-work", serverUrl: "/api/amazon/scheduling/home/v2/unassign-work", skipQueryParams: true, sendPayloadAsRequestBody: true },
  { awsUrl: "/scheduling/home/api/v2/createDAAssignments", serverUrl: "/api/amazon/scheduling/home/v2/createDAAssignments", skipQueryParams: true },
  { awsUrl: "/scheduling/api/updateDARouteAssignment", serverUrl: "/api/amazon/scheduling/updateDARouteAssignment", skipQueryParams: true, sendPayloadResponseAsRequestBody: true },
  { awsUrl: "/scheduling/api/cancelDAAssignments", serverUrl: "/api/amazon/scheduling/cancelDAAssignments", skipQueryParams: true, sendPayloadResponseAsRequestBody: true },
  { awsUrl: "/training-scheduler/search-da/", serverUrl: "/api/amazon/training-scheduler/search-da", skipQueryParams: true },
  { awsUrl: "/scheduling/api/getProviderReservationAssignmentsByDspAndServiceArea", serverUrl: "/api/amazon/scheduling/getProviderReservationAssignmentsByDspAndServiceArea", skipQueryParams: false, queryParamsKeys: ["providerId", "serviceAreaId", "date"] },
  { awsUrl: "/account-management/data/getOperationalStatusByProviderId", serverUrl: "/api/amazon/account-management/data/getOperationalStatusByProviderId", skipQueryParams: false },
  { awsUrl: "/scheduling/home/api/v2/weekly-summary", serverUrl: "/api/amazon/scheduling/home/v2/weekly-summary", skipQueryParams: false },
  { awsUrl: "/performance/api/v1/getData", serverUrl: "/api/amazon/performance/getData", skipQueryParams: false },
  { awsUrl: "/training-scheduler/transporter/unregister-driver", serverUrl: "/api/amazon/training-scheduler/transporter/unregister-driver", skipQueryParams: true, sendPayloadAsRequestBody: true },
  {
    awsUrl: "/operations/execution/api/route-details",
    serverUrl: "/api/amazon/operations/execution/route-details",
    skipQueryParams: false,
    keys: {
      rmsRouteDetails: ["routeCode", "unknownStops", "plannedOutboundStemTime", "actualOutboundStemTime", "sessionEndTime"]
    }
  },
  {
    awsUrl: "/operations/execution/api/itineraries",
    serverUrl: "/api/amazon/operations/execution/itineraries",
    skipQueryParams: false,
    keys: {
      itineraryDetails: ["transporterId", "unknownStops", "sessionEndTime"]
    }
  },
  {
    awsUrl: "/operations/execution/api/routes",
    serverUrl: "/api/amazon/operations/execution/routes",
    skipQueryParams: true,
    keys: {
      routePlan: ["unknownStops"]
    }
  },
  { awsUrl: "/supportcentral/api/proxy/services/data/v57.0/query", serverUrl: "/api/amazon/supportcentral/proxy/services/data/v57.0", skipQueryParams: false },
  { awsUrl: "/scheduling/api/getRouteInfoByProviderAndServiceArea", serverUrl: "/api/amazon/scheduling/getRouteInfoByProviderAndServiceArea", skipQueryParams: false, queryParamsKeys: ["serviceAreaId", "date"] },
  { awsUrl: "/fleet-management/api/v2/capacity", serverUrl: "/api/amazon/fleet-management/v2/capacity", skipQueryParams: false },
  { awsUrl: "/scheduling/home/api/v2/work-buckets", serverUrl: "/api/amazon/scheduling/home/v2/work-buckets", skipQueryParams: false },
  { awsUrl: "/flexpayments/simpson/flexpro/invoices/getAllInvoices", serverUrl: "/api/amazon/flexpayments/simpson/flexpro/invoices/getAllInvoices", skipQueryParams: false },
  { awsUrl: "/flexpayments/simpson/flexpro/invoices/invoiceSummary", serverUrl: "/api/amazon/flexpayments/simpson/flexpro/invoices/invoiceSummary", skipQueryParams: false },
  { awsUrl: "/flexpayments/simpson/flexpro/invoices/listRateCards", serverUrl: "/api/amazon/flexpayments/simpson/flexpro/invoices/listRateCards", skipQueryParams: false },
  { awsUrl: "/fleet-management/api/vehicle-audit-events", serverUrl: "/api/amazon/fleet-management/vehicle-audit-events", skipQueryParams: false },
  { awsUrl: "/scheduling/home/api/v2/route-plan", serverUrl: "/api/amazon/scheduling/home/v2/route-plan", skipQueryParams: false },
  { awsUrl: "/scheduling/api/getProviderReservation", serverUrl: "/api/amazon/scheduling/getProviderReservation", skipQueryParams: false, queryParamsKeys: ["providerId", "serviceAreaId", "dates"] },
  { awsUrl: "/fleet-management/api/inspection-details", serverUrl: "/api/amazon/fleet-management/inspection-details", skipQueryParams: false },
  { awsUrl: "/harmony/api/v2/worksheets", serverUrl: "/api/amazon/harmony/v2/worksheets", skipQueryParams: false },
  { awsUrl: "/fleet-management/api/afs-count", serverUrl: "/api/amazon/fleet-management/afs-count", skipQueryParams: false },
  { awsUrl: "/flexpayments/simpson/flexpro/invoices/getDisputableVehicleDetails", serverUrl: "/api/amazon/flexpayments/simpson/flexpro/invoices/getDisputableVehicleDetails", skipQueryParams: false },
  { awsUrl: "/fleet-management/api/inspections/*", serverUrl: "/api/amazon/fleet-management/inspections/*", skipQueryParams: false, includeTimezone: true}
];

// Active polling configurations (triggered after page load)
// Each entry defines a set of Amazon APIs to poll with specific parameters and timing
export const EXTENSION_SYNC_CONDITIONAL_TRACK_URLS: ConditionalTrackConfig[] = [
  {
    amazonSyncURL: [
      // Daily CDF
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_daily_cdf_deep_dive", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          from: { offset: [1, 2, 3] },
          to: { offset: [1, 2, 3] },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Daily" }
        },
        method: "GET",
        sequenceNumber: 0
      },
      // Daily DSB
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_station_daily_dsb_dnr_tba", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          from: { offset: [1, 2, 3] },
          to: { offset: [1, 2, 3] },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Daily" }
        },
        method: "GET",
        sequenceNumber: 1
      },
      // Daily DCR
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_daily_rts_deep_dive", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          from: { offset: [1, 2, 3] },
          to: { offset: [1, 2, 3] },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Daily" }
        },
        method: "GET",
        sequenceNumber: 2
      },
      // Daily Safety OSS
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_station_daily_safety_oss_events_intraday", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          dspId: "organizationDetails.companyId",
          from: { offset: [1, 2, 3] },
          to: { offset: [1, 2, 3] },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Daily" }
        },
        method: "GET",
        sequenceNumber: 3
      },
      // Weekly CDF — Wednesday and Thursday only
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_station_weekly_quality", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          from: { offset: [1], unit: "week" },
          to: { offset: [1], unit: "week" },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Weekly" },
          program: { value: "AMZL" }
        },
        method: "GET",
        allowedDays: ["wednesday", "thursday"],
        sequenceNumber: 4
      },
      // Weekly Supplemental Quality — Wednesday and Thursday only
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_station_weekly_supplemental_quality", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          from: { offset: [1], unit: "week" },
          to: { offset: [1], unit: "week" },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Weekly" }
        },
        method: "GET",
        allowedDays: ["wednesday", "thursday"],
        sequenceNumber: 5
      },
      // Weekly Station Performance — Wednesday and Thursday only
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_station_weekly_performance", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          from: { offset: [1], unit: "week" },
          to: { offset: [1], unit: "week" },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Weekly" },
          dspId: "organizationDetails.companyId",
          program: { value: "AMZL" }
        },
        method: "GET",
        allowedDays: ["wednesday", "thursday"],
        sequenceNumber: 6
      },
      // Weekly Station Safety — Wednesday and Thursday only
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_station_weekly_safety_oss_v2", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          from: { offset: [1], unit: "week" },
          to: { offset: [1], unit: "week" },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Weekly" },
          dspId: "organizationDetails.companyId"
        },
        method: "GET",
        allowedDays: ["wednesday", "thursday"],
        sequenceNumber: 7
      },
      // Weekly RTS Deep Dive — Wednesday and Thursday only
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_weekly_rts_deep_dive", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          from: { offset: [1], unit: "week" },
          to: { offset: [1], unit: "week" },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Weekly" }
        },
        method: "GET",
        allowedDays: ["wednesday", "thursday"],
        sequenceNumber: 8
      },
      // Weekly CDF Deep Dive — Wednesday and Thursday only
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_weekly_cdf_deep_dive", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          from: { offset: [1], unit: "week" },
          to: { offset: [1], unit: "week" },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Weekly" }
        },
        method: "GET",
        allowedDays: ["wednesday", "thursday"],
        sequenceNumber: 9
      },
      // Capacity (fleet) — Sunday, Monday, Tuesday only
      {
        urlTemplate: "/fleet-management/api/v2/capacity",
        params: {
          year: { offset: [1], unit: "weekYear", includeInSyncKey: true },
          weekNumber: { offset: [1], unit: "weekNumber", includeInSyncKey: true }
        },
        method: "GET",
        allowedDays: ["sunday", "monday", "tuesday"],
        sequenceNumber: 10
      },
      // Fleet Vehicles (all statuses) — every day
      {
        urlTemplate: "/fleet-management/api/vehicles",
        params: {
          vehicleStatuses: { value: "ACTIVE,MAINTENANCE,PENDING,INACTIVE" }
        },
        method: "GET",
        sequenceNumber: 11
      },
      // Active Employees — every day
      {
        urlTemplate: "/workforce/api/v1/fetchDSPAssociates",
        params: {
          companyId: "organizationDetails.companyId",
          operationalStatuses: { value: "ACTIVE,INACTIVE,OFFBOARDED" },
          station: "organizationDetails.stationCodes[].serviceAreaName"
        },
        method: "GET",
        sequenceNumber: 12
      },
      // Onboarded Employees — every day
      {
        urlTemplate: "/dsp-account-management/data/get-da-onboarding-data",
        params: {
          providerId: "organizationDetails.providerId",
          serviceAreaId: "organizationDetails.stationCodes[].serviceAreaId"
        },
        method: "GET",
        sequenceNumber: 13
      },
      // Working Device Trips (Netradyne) — every day
      //
      // WHAT: Polls Amazon's /performance/api/v1/getData for the
      // da_dsp_station_working_device_trips_intraday dataset at T-1, T-2, and T-3
      // per service area. The backend route handler dispatches this dataSetId to
      // upsertDaDspStationWorkingDeviceTripsIntradayData, which creates "netradyne"
      // fleet notes for vehicles with negative device impact (impact_wdm = "Y").
      // The upsert function applies a 7-day age guard, all-time natural-key dedup,
      // and employee attribution via transporter_id → employee.id batch lookup.
      //
      // WHY sequenceNumber: 14 (NOT 15):
      // The snake loop has a hard invariant — the entry with `executionTimeInterval`
      // (the real-time safety OSS config below) MUST always be the highest sequence
      // number (the tail of the snake). Nothing can come after it. If a normal batch
      // config is placed after the executionTimeInterval config, the snake loop gets
      // stuck: when handleSafetyDataSync() returns a no-op (routes not active or
      // cooldown active), lastAmazonSync is NOT advanced, so the loop retries the
      // safety entry forever and never reaches the config after it. Even when safety
      // does fire, the loop enters "realtime tail mode" which skips all
      // non-executionTimeInterval configs. So a normal config at seq 15 is unreachable
      // in every scenario. Placing Netradyne at seq 14 (before the safety tail at
      // seq 15) ensures it runs as a normal batch entry and is always reachable.
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "da_dsp_station_working_device_trips_intraday", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          dspId: "organizationDetails.companyId",
          from: { offset: [1, 2, 3] },
          to: { offset: [1, 2, 3] },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Daily" }
        },
        method: "GET",
        sequenceNumber: 14
      },
      // Real-time safety OSS events — called by handleSafetyDataSync on its own
      // executionTimeInterval (30 min) when routes are DEPARTED/IN_PROGRESS.
      //
      // WHAT: Polls Amazon's /performance/api/v1/getData for the
      // dsp_station_daily_safety_oss_events_real_time dataset at T-0 (today).
      // The executionTimeInterval field tells the extension to poll this entry on
      // its own timer (every 30 min) instead of the default snake loop cadence.
      // handleSafetyDataSync looks this up via getSafetyRealtimeConfig().
      //
      // WHY sequenceNumber: 15 (MUST be the tail — highest sequence number):
      // The snake loop architecture requires that the executionTimeInterval config
      // always be the last entry. When the snake reaches this entry, it delegates
      // to handleSafetyDataSync(). On a no-op (routes not active or cooldown active),
      // lastAmazonSync is intentionally NOT advanced — the loop retries this entry
      // on the next tick. If any normal batch config were placed after this one,
      // it would never be reached. After safety fires, the loop writes
      // DONE_FOR_TODAY with maxSeq and enters "realtime tail mode" which skips all
      // non-executionTimeInterval configs. So this entry MUST be the tail.
      //
      // WHY the sequence number change from 14 to 15 is safe:
      // getSafetyRealtimeConfig() finds this config by matching dataSetId
      // ("dsp_station_daily_safety_oss_events_real_time"), NOT by sequence number.
      // Moving it from 14 to 15 does not break the lookup. The executionTimeInterval
      // behavior (independent 30-min timer) is unaffected by the sequence number —
      // it only affects the entry's position in the snake loop's batch ordering.
      {
        urlTemplate: "/performance/api/v1/getData",
        params: {
          dataSetId: { value: "dsp_station_daily_safety_oss_events_real_time", includeInSyncKey: true },
          dsp: "organizationDetails.shortCode",
          dspId: "organizationDetails.companyId",
          from: { offset: [0] },
          to: { offset: [0] },
          station: "organizationDetails.stationCodes[].serviceAreaName",
          timeFrame: { value: "Daily" }
        },
        method: "GET",
        sequenceNumber: 15,
        executionTimeInterval: 30
      }
    ]
  }
];

// daily operation url config 
export const DAILY_OPERATION_CONFIG = {
  urls: [
    "/operations/execution/api/route-summaries",
    "/operations/execution/api/summaries"
  ],
  delayInMinutes: 10
};

// ============================================================================
// Pay-code constants
//
// Central source of truth for pay codes that require special handling across
// the Daily Operation page, Payroll → Timecard page, and the backend services
// that feed them. Both the UI components and the backend services import from
// here so the classification never drifts between layers.
//
// All comparisons against these codes MUST be case-insensitive.
// ============================================================================

/**
 * Pay codes that represent Unpaid Time Off.
 *
 * Hours logged under these codes:
 *   - Are displayed as their own "Unpaid Time Off" line item in Non-Route
 *     Hours / Shift Breakdown on the Daily Operation page.
 *   - Are EXCLUDED from the Payroll Used KPI and Labor Delta.
 *   - Are EXCLUDED from the weekly overtime threshold (40-hr) on both the
 *     Daily Operation page and the Payroll → Timecard page.
 *
 * "TIME OFF REQUEST" is the most common offender;
 * the remaining codes cover the variants used by different payroll providers
 * (e.g. SNDL uses "UNPAID PTO").
 */
export const UNPAID_TIME_OFF = [
  "TIME OFF REQUEST",
  "UNPAID PTO",
  "UNPAID SICK",
  "UNPAID VACATION",
  "UPO",
  "UPT",
] as const;

// ============================================================================
// Driver RTS confirmation flow (Return to Station page)
// ============================================================================

// The driver's answer to one expected-return package.
// confirmed = agreed with Amazon's recorded reason;
// disputed = driver picked a different reason (note required);
// driver_reason = Amazon recorded no reason, driver picked one.
export const RTS_RESPONSE = {
  CONFIRMED: "confirmed",
  DISPUTED: "disputed",
  DRIVER_REASON: "driver_reason",
} as const;

// Reason-picker taxonomy shown in the "Why is it coming back?" sheet.
// The first entries match the mockup's ranked rows; the rest reuse the DCR
// acceptedValues vocabulary already used by corrective actions so the codes
// stay consistent with the language ops uses elsewhere.
export const RTS_RETURN_REASONS = [
  { code: "BUSINESS_CLOSED", label: "Business closed", rank: "MOST USED" },
  { code: "NOT_ON_ROUTE", label: "Not on my route", rank: "2ND" },
  { code: "CANT_ACCESS", label: "Can't get in — gate or code", rank: "3RD" },
  { code: "ADDRESS_NOT_FOUND", label: "Address wrong or not found" },
  { code: "CUSTOMER_UNAVAILABLE", label: "Customer unavailable" },
  { code: "INACCESSIBLE", label: "Inaccessible delivery location" },
  { code: "NO_SECURE_LOCATION", label: "No secure location" },
  { code: "UNSAFE_DOG", label: "Unsafe — dog" },
  { code: "BAD_WEATHER", label: "Bad weather" },
  { code: "OTHER", label: "Something else", rank: "OTHER" },
] as const;